import { Component, NgZone, OnInit, OnDestroy, Input } from '@angular/core';
import { FormBuilder, UntypedFormControl, UntypedFormGroup, Validators, FormControl } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router, NavigationEnd } from '@angular/router';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { Constant } from 'src/app/constant/constant';
import { AccountsService } from 'src/app/services/accounts.service';
import { CommonService } from 'src/app/services/common.service';
import { LoaderService } from 'src/app/services/loader.service';
import { SubscriptionsService } from 'src/app/services/subscriptions.service';
import { CountryStateCityService } from 'src/app/services/country-state-city.service';
import { CountryLocationService } from 'src/app/services/country-location.service';
import { EcryptServiceService } from 'src/app/core/auth/EcryptionService/ecrypt-service.service';
import { noWhitespaceValidator } from 'src/app/shared/utils/calsoft-validators';

@Component({
  selector: 'calsoft-renewal',
  templateUrl: './renewal.component.html',
  styleUrls: ['./renewal.component.scss']
})
export class RenewalComponent implements OnInit, OnDestroy {

  trialExpiredMessage: any;

  url = '';
  sanitizedUrl = '';
  upgradePlantype: boolean = false;
  highlightPlanImage: number = 0;
  plan: any;
  currencySymbol: string = '₹';
  currencyCode: string = 'INR';
  userNewlyAdded: boolean = false;
  showRenwal: boolean = false;
  forcedOperationType: string | null = null;
  pendingPlanSelectionState: any = null;

  get currency(): string {
    return this.currencyCode;
  }



  constructor(private _formBuilder: FormBuilder,
    private snackBar: MatSnackBar,
    private router: Router,
    private subscriptionsService: SubscriptionsService,
    private commonService: CommonService,
    private loaderService: LoaderService,
    private _activatedRoute: ActivatedRoute,
    private accountService: AccountsService,
    private zone: NgZone,
    private countryStateCityService: CountryStateCityService,
    private countryLocationService: CountryLocationService,
    private ecryptService: EcryptServiceService
  ) {
    this.url = this.router.url || '';
    this.sanitizedUrl = this.sanitizeUrl(this.url);
    this.router.events.pipe(filter((e: any) => e instanceof NavigationEnd), takeUntil(this._onDestroy)).subscribe(() => {
      this.url = this.router.url || '';
      this.sanitizedUrl = this.sanitizeUrl(this.url);
    });

    this.url = this.router.url || '';
    this.router.events
      .pipe(filter((e: any) => e instanceof NavigationEnd), takeUntil(this._onDestroy))
      .subscribe(() => { this.url = this.router.url || ''; });
  }
  get isNewRoute(): boolean {
    return this.router.url === '/new' || this.router.url === '/subscription/checkout';
  }

  get isSubscriptionManageRoute(): boolean {
    return this.router.url === '/subscription/manage';
  }

  // Handlers for renew-active child events
  onChildChangePlan() {
    this.pendingAddonOnly = false;
    this.subscriptionsService.navigateToPlanSelection({
      source: 'renewal-component',
      currentPricingId: this.selectedPricingId || this.subscriptionSummary?.pricingId,
      currentBillingCycle: this.billingCycle || this.subscriptionSummary?.billingCycle,
      currentUsers: Number(this.firstFormGroup?.get('users')?.value || this.currentUsers || this.subscriptionSummary?.activeUsers || 1),
      daysRemaining: this.daysRemaining || this.subscriptionSummary?.daysRemaining,
      productId: this.productId,
      tenantId: this.subscriptionSummary?.tenantId
    });
  }

  onChildAddUsers() {
    // retained for compatibility; child now handles add-users UI/modal.
    console.log('Add users requested from child (handled in child component)');
  }

  onChildUsersAdded(response: any) {
    // After child notifies that users were added, refresh subscription state and preview
    try {

      try { this.loadBillingDetailsFromAccount(); } catch (e) { /* swallow if not ready */ }
      // If child indicated updateUser, set local flag
      if (response && response.updateUser) {
        this.updateUser = true;
      }
      // Try to merge returned subscription data if present
      try {
        const respObj = response && response.response ? response.response : response;
        if (respObj && respObj.data) {
          this.subscriptionSummary = Object.assign({}, this.subscriptionSummary || {}, respObj.data || {});
        } else if (respObj && respObj.subscription) {
          this.subscriptionSummary = Object.assign({}, this.subscriptionSummary || {}, respObj.subscription || {});
        }
      } catch (mergeErr) {
        console.warn('Failed to merge child usersAdded response', mergeErr);
      }
      // Close any upgrade child UI and show payment section so user can complete subscription for existing account
      try {
        this.upgradePlantype = false;
        this.subscribtionType = 2; // ensure renew flow so purchase controls are visible
        this.showPaymentOptions = true;
        // if subscriptionSummary contains updated users, patch users control for immediate display
        const usersFromSummary = Number(this.subscriptionSummary?.totalLicensedUsers || this.subscriptionSummary?.activeUsers || 0);
        if (usersFromSummary && this.firstFormGroup) {
          this.firstFormGroup.patchValue({ users: usersFromSummary }, { emitEvent: false });
        }
      } catch (e) { /* ignore */ }

      // Refresh authoritative subscription state and preview
      this.loadSubscriptionState();
      this.refreshPreview();

      this.snackBar.open('Users added — complete payment below to update subscription', '×', { duration: 4000 });
    } catch (e) {
      console.warn('Error refreshing after users added', e);
    }
  }

  onChildManageSubscription() {
    console.log('Manage subscription requested from child');
    // TODO: navigate to management UI or open drawer
  }

  onChildAddUsersProceed(event: any) {
    this.pendingAddonOnly = false;
    try { this.loadBillingDetailsFromAccount(); } catch (e) { /* swallow if not ready */ }
    this.userNewlyAdded = true;
    // Child requested parent to perform payment for additional users
    try {
      const addCount = Number(event?.addUserCount || 0);
      if (addCount <= 0) return;
      // close child upgrade state and show payment UI
      this.upgradePlantype = false;
      this.subscribtionType = 2;
      this.showRenwal = true;
      this.showPaymentOptions = true;
      // store pending additional users and preview/charge so payment handler can construct correct payload
      this.pendingAddUsers = addCount;
      this.pendingAddUsersCharge = Number(event?.addUserCharge || 0) || 0; // amount excluding GST
      this.pendingAddUsersTax = Number(event?.addUserTax || 0) || 0;
      this.pendingAddUsersPreview = event?.preview || null;
      // Do NOT patch the users form control here — keep showing current users and display "Additional Users" separately
      // refresh preview so UI shows accurate totals
      this.refreshPreview();
      this.snackBar.open('Proceed to payment for additional users — complete payment below', '×', { duration: 4000 });
    } catch (e) {
      console.warn('onChildAddUsersProceed failed', e);
    }
  }

  onChildRenewWithAddons(event: any) {
    try { this.loadBillingDetailsFromAccount(); } catch (e) { /* swallow */ }
    try {
      const selections: any[] = event?.selections || [];
      // Populate billingAddonSelections so checkout includes the new addons
      if (selections.length > 0) {
        this.billingAddonSelections = selections.map((s: any) => ({
          addon: s.addon,
          quantity: s.quantity,
          subtotal: Number(s.proratedSubtotal || 0),
          gst: Number(s.gstAmount || 0),
          total: Number(s.total || 0)
        }));
      }
      // Flag: addon-only mid-cycle purchase — do NOT include plan renewal price
      this.pendingAddonOnly = true;
      this.pendingAddUsers = 0;
      // Commitment term: today → current subscription end date
      this.startDate = new Date();
      const subEnd = this.subscriptionSummary?.endDate
        || this.subscriptionSummary?.end_date
        || this.subscriptionSummary?.renewalDate
        || this.subscriptionSummary?.renewal_date
        || this.subscriptionSummary?.nextPaymentDate
        || this.subscriptionSummary?.next_payment_date;
      this.endDate = subEnd ? new Date(subEnd) : (() => { const d = new Date(); d.setFullYear(d.getFullYear() + 1); return d; })();
      if (isNaN(this.endDate.getTime())) { const d = new Date(); d.setFullYear(d.getFullYear() + 1); this.endDate = d; }
      // Show billing/payment section inline
      this.upgradePlantype = false;
      // Do NOT set userNewlyAdded — showRenwal alone shows checkout; userNewlyAdded=true hides commitment term
      this.subscribtionType = 2;
      this.showRenwal = true;
      this.showPaymentOptions = true;
      this.snackBar.open('Add-ons selected — complete payment below', '×', { duration: 4000 });
    } catch (e) {
      console.warn('onChildRenewWithAddons failed', e);
    }
  }

  onChildPaymentHistory() {
    console.log('Payment history requested from child');
    // Example: navigate to payment history page if available
    // this.router.navigate(['/billing/history']);
  }

  onChildEditBilling() {
    console.log('Edit billing requested from child');
    // Enter upgrade/inline-edit billing flow: load account billing, show payment UI, allow inline edits and payment
    try {
      // ensure UI shows the upgrade/edit path
      this.upgradePlantype = true;
      this.subscribtionType = 2; // treat as renew flow so payment section is visible
      this.showPaymentOptions = true;
      // patch billing details from account and latest payment record
      this.loadBillingDetailsFromAccount();
      // ensure users control initialised and preview updated
      this.initializeUsers();
      this.refreshPreview();
      // scroll billing form into view if possible
      try {
        setTimeout(() => {
          const el = document.querySelector('[formgroup]') || document.querySelector('.form-container1');
          if (el && (el as HTMLElement).scrollIntoView) (el as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
      } catch (e) { /* ignore */ }
    } catch (e) {
      console.warn('Failed to open billing edit flow', e);
    }
  }

  onChildChangeBillingType(type: string) {

    this.upgradePlantype = true;
    // request UI to highlight plan image #2 when upgrade flow begins
    this.highlightPlanImage = 2;
    // ensure billing form is patched from account data for the upgrade flow
    try { this.loadBillingDetailsFromAccount(); } catch (e) { /* swallow if not ready */ }

    console.log('Change billing type requested from child:', type);
    // If this flow was initiated as part of adding users, handle slightly differently
    if (this.updateUser) {
      // apply billing change for the add-user flow
      this.updateUser = false; // consume the flag
      this.billingCycle = type.toLowerCase();
      this.firstFormGroup.patchValue({ paymentGateway: this.firstFormGroup.get('paymentGateway')?.value || 'razor' });
      this.updatePrice(this.billingCycle);
      this.refreshPreview();
      this.snackBar.open('Billing type updated as part of user update', '×', { duration: 3000 });
      return;
    }
    const current = (this.subscriptionSummary?.billingCycle || this.billingCycle || '').toLowerCase();
    const newCycle = type.toLowerCase();
    if (newCycle === current) {
      this.snackBar.open('Already on ' + type + ' billing', '×', { duration: 2000 });
      return;
    }

    // compute a simple prorated estimate for upgrade charge
    const currentPrice = Number(this.availableMonthlyPrice || 0);  // monthly rate used as base
    const targetAnnual = Number(this.availableAnnualPrice || 0);
    const daysRem = Number(this.daysRemaining || this.subscriptionSummary?.daysRemaining || 0);

    // fallback if no pricing available
    if (currentPrice <= 0 && targetAnnual <= 0) {
      if (!confirm('No pricing available to compute upgrade. Proceed to change billing type?')) {
        return;
      }
      this.updatePrice(newCycle);
      return;
    }

    // estimate credit for remaining current cycle period
    const currentCycleDays = this.getCycleDays(current);
    const remainingFraction = Math.max(0, Math.min(1, daysRem / Math.max(currentCycleDays, 1)));
    const credit = currentPrice * remainingFraction;
    // target price — for quarterly/half-yearly use monthly*months as estimate
    let targetPrice: number;
    switch (newCycle.toUpperCase()) {
      case 'QUARTERLY':   targetPrice = currentPrice * 3; break;
      case 'HALF_YEARLY': targetPrice = currentPrice * 6; break;
      case 'ANNUALLY':    targetPrice = targetAnnual > 0 ? targetAnnual : currentPrice * 12; break;
      default:            targetPrice = currentPrice; break; // MONTHLY
    }
    let upgradeAmount = targetPrice - credit;
    if (upgradeAmount < 0) upgradeAmount = 0;

    const msg = `Switching from ${current} to ${type} will charge approximately ${this.currencySymbol}${upgradeAmount.toFixed(2)} (estimated). Proceed?`;
    if (confirm(msg)) {
      // perform change locally: update billingCycle & refresh preview
      this.billingCycle = newCycle;
      this.firstFormGroup.patchValue({ paymentGateway: this.firstFormGroup.get('paymentGateway')?.value || 'razor' });
      this.updatePrice(this.billingCycle);
      this.refreshPreview();
      this.snackBar.open('Billing type updated to ' + this.billingCycle, '×', { duration: 3000 });
    }
  }
  firstFormGroup!: UntypedFormGroup;

  textAccount: any;

  startDate: Date = new Date();
  endDate: Date = new Date();

  billingCycle: string = 'annually'; // Default cycle
  perMonthPrice: number = 0;
  perYearPrice: number = 0;
  totalPrice: number = 0;
  gstAmount: number = 0;
  grandTotal: number = 0;

  pricingList: any[] = [];
  selectedPricingId: number | null = null;
  subscriptionSummary: any = null;
  @Input() subscriptionLoaded: boolean = false;
  pricingPreview: any = null;
  currentUsers: number = 0;
  productId: number = 1;
  isPreviewLoading: boolean = false;
  isUnlimitedPlan: boolean = false;
  isFreePlan: boolean = false;
  displayPlanPrice: number = 0;
  hasActiveSubscription: boolean = false;
  @Input() accountLoaded: boolean = false;
  @Input() embeddedState: any = null;
  showPurchase: boolean = false;
  accountUsersFromApi: number | null = null;
  availableMonthlyPrice: number = 0;
  availableAnnualPrice: number = 0;
  daysRemaining: number = 0;
  renewalThresholdDays: number = 31;
  canRenew: boolean = true;

  subscribtionType: any = 1;

  // Add-users modal state
  addUser: boolean = false;
  addUserCount: number = 1;
  addUserCharge: number = 0;
  addUserTax: number = 0;
  addUserCheckoutInProgress: boolean = false;
  // flag used when users were added in child; controls special billing-type flow
  updateUser: boolean = false;
  // local edit guard: when user is typing a new users value we show client-side totals
  pendingLocalUserChange: boolean = false;
  // when parent is asked to accept payment for add-users, store pending additional users here
  pendingAddUsers: number = 0;
  pendingAddUsersCharge: number = 0;
  pendingAddUsersPreview: any = null;
  pendingAddUsersTax: number = 0;
  pendingAddonOnly: boolean = false;  // true when paying only for new add-ons mid-cycle
  planSelectionCheckoutPreview: any = null;
  
  get canShowPayment(): boolean {
    return this.computedGrandTotal > 0;
  }

  // Mobile Checkout Step
  mobileCheckoutStep = 1;

  goToNextMobileStep(): void {
    let nextStep = this.mobileCheckoutStep;
    if (this.mobileCheckoutStep === 1) {
      if (this.firstFormGroup.invalid) {
        this.firstFormGroup.markAllAsTouched();
        return;
      }
      nextStep = 2;
    } else if (this.mobileCheckoutStep === 2) {
      if (this.billingAddons.length > 0 || this.billingAddonsLoading) {
        nextStep = 3;
      } else {
        nextStep = 4;
      }
    } else if (this.mobileCheckoutStep === 3) {
      nextStep = 4;
    } else if (this.mobileCheckoutStep === 4) {
      nextStep = 5;
    }
    
    this.router.navigate([], {
      queryParams: { step: nextStep },
      queryParamsHandling: 'merge'
    }).then(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }

  goToPrevMobileStep(): void {
    let prevStep = this.mobileCheckoutStep;
    if (this.mobileCheckoutStep === 5) {
      prevStep = 4;
    } else if (this.mobileCheckoutStep === 4) {
      if (this.billingAddons.length > 0 || this.billingAddonsLoading) {
        prevStep = 3;
      } else {
        prevStep = 2;
      }
    } else if (this.mobileCheckoutStep === 3) {
      prevStep = 2;
    } else if (this.mobileCheckoutStep === 2) {
      prevStep = 1;
    }
    
    this.router.navigate([], {
      queryParams: { step: prevStep },
      queryParamsHandling: 'merge'
    }).then(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }

  goBackToPlanSelection(): void {
    try {
      sessionStorage.removeItem('pending_checkout_state');
    } catch (e) {}
    this.router.navigate(['/subscription/plan-selection']);
  }

  // ---- Add-on picker state (pizza-style selector on billing screen) ----
  billingAddons: any[] = [];            // full catalogue from API
  billingAddonsLoading = false;
  billingAddonSelections: Array<{ addon: any; quantity: number; subtotal: number; gst: number; total: number }> = [];

  get billingAddonSubtotal(): number {
    return this.billingAddonSelections.reduce((s, a) => s + a.subtotal, 0);
  }

  get billingAddonGst(): number {
    return this.billingAddonSelections.reduce((s, a) => s + a.gst, 0);
  }

  get billingAddonTotal(): number {
    return Number((this.billingAddonSubtotal + this.billingAddonGst).toFixed(2));
  }

  /** Returns the cyclePrices entry matching the current billingCycle (case-insensitive). */
  getCyclePriceForAddon(addon: any): any {
    const cycle = String(this.billingCycle || this.subscriptionSummary?.billingCycle || '').toUpperCase();
    const prices: any[] = addon?.cyclePrices || [];
    return prices.find((p: any) => String(p.billingCycle || '').toUpperCase() === cycle)
        ?? prices[0]
        ?? null;
  }

  isPlanTypeBilling(addon: any): boolean {
    return String(addon?.addonType || '').toUpperCase() === 'PLAN';
  }

  isPlanSelectedInBilling(): boolean {
    return this.billingAddonSelections.some(s => this.isPlanTypeBilling(s.addon));
  }

  isAddonSelectedInBilling(addonCode: string): boolean {
    return this.billingAddonSelections.some(s => s.addon.addonCode === addonCode);
  }

  isPlanAddonDisabledInBilling(addon: any): boolean {
    return this.isPlanTypeBilling(addon) && this.isPlanSelectedInBilling() && !this.isAddonSelectedInBilling(addon.addonCode);
  }

  onToggleBillingAddon(addon: any, checked: boolean): void {
    if (checked) {
      const sel = { addon, quantity: Number(addon.minQuantity || 1), subtotal: 0, gst: 0, total: 0 };
      this.computeBillingAddonAmounts(sel);
      this.billingAddonSelections = [...this.billingAddonSelections, sel];
    } else {
      this.billingAddonSelections = this.billingAddonSelections.filter(s => s.addon.addonCode !== addon.addonCode);
    }
  }

  onBillingAddonQtyChange(sel: any, qty: number): void {
    const min = Number(sel.addon.minQuantity || 1);
    const max = sel.addon.maxQuantity ? Number(sel.addon.maxQuantity) : null;
    sel.quantity = Math.max(min, Number.isFinite(qty) && qty > 0 ? qty : min);
    if (max !== null) sel.quantity = Math.min(sel.quantity, max);
    this.computeBillingAddonAmounts(sel);
    this.billingAddonSelections = [...this.billingAddonSelections];
  }

  private computeBillingAddonAmounts(sel: any): void {
    const cp = this.getCyclePriceForAddon(sel.addon);
    const unitPrice = Number(cp?.unitPrice ?? 0);
    const gstPct = Number(cp?.gstPercentage ?? 18);
    const daysRem = Number(this.daysRemaining || this.subscriptionSummary?.daysRemaining || 0);
    const cycle = String(this.billingCycle || cp?.billingCycle || '').toUpperCase();
    const cycleDays = this.getCycleDays(cycle);
    // Trial accounts and new subscriptions → full cycle price (no pro-ration)
    const subMode = String(this.subscriptionSummary?.subscriptionMode || '').toUpperCase();
    const isTrialOrNew = subMode === 'TRIAL' || subMode === 'NEW' || this.isPlanSelectionCheckoutFlow;
    const isRenewal = !isTrialOrNew && (this.showRenwal || subMode === 'ACTIVE');
    const prorata = isRenewal ? Math.min(1, Math.max(0, daysRem / Math.max(cycleDays, 1))) : 1;
    const subtotal = Number((unitPrice * sel.quantity * prorata).toFixed(2));
    sel.subtotal = subtotal;
    sel.gst = Number((subtotal * gstPct / 100).toFixed(2));
    sel.total = Number((sel.subtotal + sel.gst).toFixed(2));
  }

  loadBillingAddons(): void {
    if (this.billingAddons.length > 0) return; // already loaded
    this.billingAddonsLoading = true;
    this.subscriptionsService.getAvailableAddons()
      .pipe(takeUntil(this._onDestroy))
      .subscribe({
        next: (res: any) => {
          const all = Array.isArray(res) ? res : (res?.data || res?.content || []);
          this.subscriptionsService.getMyAddons()
            .pipe(takeUntil(this._onDestroy))
            .subscribe({
              next: (activeRes: any) => {
                const activeAddons = Array.isArray(activeRes) ? activeRes : (activeRes?.data || activeRes?.content || []);
                this.billingAddons = all.filter(a =>
                  !activeAddons.some((active: any) => active.addonCode === a.addonCode)
                );
                this.billingAddonsLoading = false;
              },
              error: () => {
                this.billingAddons = all;
                this.billingAddonsLoading = false;
              }
            });
        },
        error: () => { this.billingAddonsLoading = false; }
      });
  }

  get planTypeAddonsForBilling(): any[] {
    return this.billingAddons.filter(a => this.isPlanTypeBilling(a));
  }

  get otherTypeAddonsForBilling(): any[] {
    return this.billingAddons.filter(a => !this.isPlanTypeBilling(a));
  }

  // Coupon state
  couponCodeInput: string = '';
  appliedCoupon: any = null;
  couponDiscount: number = 0;
  couponApplying: boolean = false;
  couponError: string = '';

  get estimatedNextPayment(): number {
    const users = Number(this.subscriptionSummary?.activeUsers || this.currentUsers || 0);
    if (this.billingCycle === 'monthly') {
      return (this.availableMonthlyPrice || 0) * users;
    }
    return (this.availableAnnualPrice || 0) * users;
  }

  get computedUsers(): number {
    const val = Number(this.firstFormGroup?.get('users')?.value ?? this.subscriptionSummary?.activeUsers ?? this.currentUsers ?? 0);
    return Number.isFinite(val) && val > 0 ? val : 0;
  }

  get computedSubtotal(): number {
    if (this.isPlanSelectionCheckoutFlow && this.planSelectionCheckoutPreview?.subtotal != null) {
      return Number((Number(this.planSelectionCheckoutPreview.subtotal || 0) + this.billingAddonSubtotal).toFixed(2));
    }
    if (this.isPlanSelectionCheckoutFlow && this.pricingPreview?.subtotal != null) {
      return Number((Number(this.pricingPreview.subtotal || 0) + this.billingAddonSubtotal).toFixed(2));
    }
    // Addon-only mid-cycle purchase: just the addon pro-rated amounts
    if (this.pendingAddonOnly) {
      return Number(this.billingAddonSubtotal.toFixed(2));
    }
    // If we are only charging for additional users, prefer the pending add-user charge
    if (this.pendingAddUsers && Number(this.pendingAddUsers) > 0) {
      return Number((Number(this.pendingAddUsersCharge || 0) + this.billingAddonSubtotal).toFixed(2));
    }
    const users = this.isUnlimitedPlan ? 1 : this.computedUsers;
    const unit = this.billingCycle === 'monthly' ? (this.availableMonthlyPrice || 0) : (this.availableAnnualPrice || 0);
    return Number(((unit * users || 0) + this.billingAddonSubtotal).toFixed(2));
  }

  get computedSubtotalAfterCoupon(): number {
    const base = this.computedSubtotal;
    const discount = Number(this.couponDiscount || 0);
    return Number(Math.max(0, base - discount).toFixed(2));
  }

  get computedPricePerUser(): number {
    return Number(this.pricingPreview?.planPricePerUser || 0);
  }

  get computedGst(): number {
    if (this.isPlanSelectionCheckoutFlow && this.planSelectionCheckoutPreview?.gstAmount != null) {
      return Number((Number(this.planSelectionCheckoutPreview.gstAmount || 0) + this.billingAddonGst).toFixed(2));
    }
    if (this.isPlanSelectionCheckoutFlow && this.pricingPreview?.gstAmount != null) {
      return Number((Number(this.pricingPreview.gstAmount || 0) + this.billingAddonGst).toFixed(2));
    }
    // Addon-only mid-cycle purchase: just the addon GST
    if (this.pendingAddonOnly) {
      return Number(this.billingAddonGst.toFixed(2));
    }
    // compute GST from pending add-user charge when present
    if (this.pendingAddUsers && Number(this.pendingAddUsers) > 0) {
      if (this.pendingAddUsersTax != null && Number(this.pendingAddUsersTax) >= 0) {
        return Number((Number(this.pendingAddUsersTax || 0) + this.billingAddonGst).toFixed(2));
      }
      const base = Number(this.computedSubtotalAfterCoupon || 0);
      const gst = Number((base * 0.18));
      return Number(isFinite(gst) ? Number(gst.toFixed(2)) : 0);
    }
    const base = Number(this.computedSubtotalAfterCoupon || 0);
    const gst = Number((base * 0.18));
    return Number(isFinite(gst) ? Number(gst.toFixed(2)) : 0);
  }

  get computedGrandTotal(): number {
    if (this.isPlanSelectionCheckoutFlow && this.planSelectionCheckoutPreview?.grandTotal != null) {
      // Add addon total (subtotal + GST) on top of the plan-only grand total from server
      return Number((Number(this.planSelectionCheckoutPreview.grandTotal || 0) + this.billingAddonTotal).toFixed(2));
    }
    if (this.isPlanSelectionCheckoutFlow && this.pricingPreview?.grandTotal != null) {
      return Number((Number(this.pricingPreview.grandTotal || 0) + this.billingAddonTotal).toFixed(2));
    }
    return Number(((Number(this.computedSubtotalAfterCoupon || 0) + Number(this.computedGst || 0))).toFixed(2));
  }

  get isPlanSelectionCheckoutFlow(): boolean {
    return !!this.pendingPlanSelectionState;
  }

  get isUpgradeFromPlanSelection(): boolean {
    return !!this.pendingPlanSelectionState
      && (this.forcedOperationType === 'UPGRADE_PLAN' || this.forcedOperationType === 'SAME_PLAN_BILLING_CHANGE');
  }

  get isRenewalFromPlanSelection(): boolean {
    return this.forcedOperationType === 'RENEW_SUBSCRIPTION' && !!this.pendingPlanSelectionState;
  }

  get isNewSubscriptionFromPlanSelection(): boolean {
    return this.forcedOperationType === 'NEW_SUBSCRIPTION' && !!this.pendingPlanSelectionState;
  }

  get isSamePlanBillingChangeFromPlanSelection(): boolean {
    return this.forcedOperationType === 'SAME_PLAN_BILLING_CHANGE' && !!this.pendingPlanSelectionState;
  }

  get planSelectionSummaryTitle(): string {
    if (this.isRenewalFromPlanSelection) {
      return 'Renewal Details';
    }
    if (this.isNewSubscriptionFromPlanSelection) {
      return 'New Subscription Details';
    }
    return this.isSamePlanBillingChangeFromPlanSelection ? 'Billing Change Details' : 'Upgrade Details';
  }

  get planSelectionTargetPlanLabel(): string {
    if (this.isRenewalFromPlanSelection) {
      return 'Renewing plan:';
    }
    if (this.isNewSubscriptionFromPlanSelection) {
      return 'Subscribing to:';
    }
    return this.isSamePlanBillingChangeFromPlanSelection ? 'Same plan:' : 'Upgrading to:';
  }

  get planSelectionProjectedValidityLabel(): string {
    if (this.isRenewalFromPlanSelection) {
      return 'Renewed valid till:';
    }
    if (this.isNewSubscriptionFromPlanSelection) {
      return 'Subscription valid till:';
    }
    return this.isSamePlanBillingChangeFromPlanSelection ? 'Updated valid till:' : 'Upgraded valid till:';
  }

  get planSelectionSubtotalLabel(): string {
    if (this.isRenewalFromPlanSelection) {
      return 'Renewal subtotal:';
    }
    if (this.isNewSubscriptionFromPlanSelection) {
      return 'Subscription subtotal:';
    }
    return this.isSamePlanBillingChangeFromPlanSelection ? 'Billing change subtotal:' : 'Upgrade subtotal:';
  }

  get showCheckoutSection(): boolean {
    const allowInlineManageCheckout = this.isPlanSelectionCheckoutFlow
      || this.showRenwal
      || this.isUpgradeFromPlanSelection
      || (!!this.pendingAddUsers && Number(this.pendingAddUsers) > 0)
      || (this.userNewlyAdded && this.showPaymentOptions);

    if (this.isSubscriptionManageRoute && !allowInlineManageCheckout) {
      return false;
    }

    return ((!this.upgradePlantype && this.userNewlyAdded
      && (!this.hasActiveSubscription || this.subscribtionType == 2))
      || this.showRenwal
      || this.isUpgradeFromPlanSelection);
  }

  get showSubscriptionBannerSection(): boolean {
    if (this.isSubscriptionManageRoute) {
      return !!(this.subscriptionSummary?.subscriptionActive || this.hasActiveSubscription || this.subscriptionLoaded);
    }

    return ((this.upgradePlantype && this.hasActiveSubscription && this.userNewlyAdded)
      || this.canRenew)
      || (!this.upgradePlantype && !this.userNewlyAdded
        && (this.subscriptionSummary?.subscriptionActive || this.hasActiveSubscription));
  }

  get showBillingDetailsHeader(): boolean {
    return !this.upgradePlantype && !this.userNewlyAdded;
  }

  get showProductsSection(): boolean {
    return !this.upgradePlantype && !this.userNewlyAdded && !this.showRenwal;
  }

  get upgradeCurrentPlanName(): string {
    return this.pendingPlanSelectionState?.currentPlanName || this.subscriptionSummary?.currentPlanName || this.subscriptionSummary?.pricingName || 'Current plan';
  }

  get upgradeTargetPlanName(): string {
    return this.pendingPlanSelectionState?.selectedPlanName
      || this.planSelectionCheckoutPreview?.pricingName
      || this.pricingPreview?.pricingName
      || this.plan?.planName
      || this.subscriptionSummary?.pricingName
      || 'Selected plan';
  }

  get upgradeSelectedUsers(): number {
    const users = Number(this.pendingPlanSelectionState?.users || this.firstFormGroup?.get('users')?.value || this.currentUsers || 1);
    return Number.isFinite(users) && users > 0 ? users : 1;
  }

  get upgradeCurrentValidityDate(): string {
    const validityDate = this.planSelectionCheckoutPreview?.currentValidityDate
      || this.pricingPreview?.currentValidityDate
      || this.pricingPreview?.currentRenewalDate
      || this.subscriptionSummary?.renewalDate
      || this.subscriptionSummary?.renewaldate
      || this.subscriptionSummary?.renewal_date;

    return this.formatDateValue(validityDate);
  }

  get upgradeProjectedValidityDate(): string {
    const validityDate = this.planSelectionCheckoutPreview?.projectedValidityDate
      || this.pricingPreview?.projectedValidityDate
      || this.pricingPreview?.nextPaymentDate
      || this.subscriptionSummary?.nextPaymentDate;

    return this.formatDateValue(validityDate);
  }

  get upgradeSubscriptionStartDate(): string {
    const startDate = this.planSelectionCheckoutPreview?.cycleStartDate
      || this.pricingPreview?.cycleStartDate
      || this.pricingPreview?.subscriptionStartDate;
    return this.formatDateValue(startDate);
  }

  get orderSummaryBillingCycle(): string {
    if (this.isPlanSelectionCheckoutFlow) {
      return String(this.pendingPlanSelectionState?.billingCycle || this.billingCycle || 'annually');
    }
    return String(this.billingCycle || 'annually');
  }

  get orderSummaryUsers(): number {
    if (this.isPlanSelectionCheckoutFlow) {
      return this.upgradeSelectedUsers;
    }
    const users = (this.pendingAddUsers && this.pendingAddUsers > 0)
      ? (this.subscriptionSummary?.activeUsers || this.currentUsers || 0)
      : (this.firstFormGroup.get('users')?.value || this.subscriptionSummary?.activeUsers || this.currentUsers || 0);
    return Number(users || 0);
  }

  get orderSummaryUsersLabel(): string {
    return this.isPlanSelectionCheckoutFlow ? 'Users' : 'Active Users';
  }

  get orderSummarySubtotalLabel(): string {
    if (this.isSamePlanBillingChangeFromPlanSelection) {
      return 'Billing change subtotal';
    }
    if (this.isRenewalFromPlanSelection) {
      return 'Renewal subtotal';
    }
    if (this.isNewSubscriptionFromPlanSelection) {
      return 'Subscription subtotal';
    }
    return this.isUpgradeFromPlanSelection ? 'Upgrade subtotal' : 'Subtotal';
  }

  get orderSummaryTotalLabel(): string {
    return this.isPlanSelectionCheckoutFlow ? 'Pay now' : 'Grand Total';
  }

  applyCheckoutCoupon(): void {
    const code = (this.couponCodeInput || '').trim();
    if (!code) return;
    this.couponError = '';
    this.couponApplying = true;
    const subtotal = this.computedSubtotal;
    this.subscriptionsService.applySubscriptionCoupon(code, subtotal).subscribe({
      next: (res: any) => {
        this.couponApplying = false;
        if (res?.valid) {
          this.appliedCoupon = res;
          this.couponDiscount = Number(res.discountAmt || 0);
          this.couponError = '';
        } else {
          this.appliedCoupon = null;
          this.couponDiscount = 0;
          this.couponError = res?.message || 'Invalid coupon code';
        }
      },
      error: () => {
        this.couponApplying = false;
        this.appliedCoupon = null;
        this.couponDiscount = 0;
        this.couponError = 'Failed to apply coupon. Please try again.';
      }
    });
  }

  removeCheckoutCoupon(): void {
    this.appliedCoupon = null;
    this.couponDiscount = 0;
    this.couponCodeInput = '';
    this.couponError = '';
  }

  // Country, State, City properties for location API
  locationCountries: any[] = [];
  locationStates: any[] = [];
  locationCities: any[] = [];

  filteredLocationCountries: any[] = [];
  filteredLocationStates: any[] = [];
  filteredLocationCities: any[] = [];

  selectedCountryId: any = null;
  selectedStateId: any = null;
  selectedCityId: any = null;

  countrySearchControl = new FormControl();
  stateSearchControl = new FormControl();
  citySearchControl = new FormControl();

  protected _onDestroy = new Subject<void>();


  ngOnInit() {

    console.log("sasa");

    this.textAccount = 'Save';
    // this.getCountryList(); // Replacing with new location logic
    // this.countrYFilter(); // Replacing with new location logic

    this.pendingAddonOnly = false;
    this.initForm();
    this.capturePlanSelectionState();

    // Initialize location country/state/city
    this.loadLocationCountries();
    this.onLocationCountrySearch();
    this.onLocationStateSearch();
    this.onLocationCitySearch();

    // Read trial message stored by layout and show to user if expired
    


    if (this.router.url.includes("renew")) {
      this.subscribtionType = 2;
    }

    this.loadSubscriptionState();
    this.loadBillingAddons();

    // Use a dedicated handler for users edits so UI updates immediately
    this.firstFormGroup.get('users')?.valueChanges.subscribe((val) => {
      this.onUsersChanged(val);
    });

    this._activatedRoute.queryParams
      .pipe(takeUntil(this._onDestroy))
      .subscribe((params) => {
        const step = Number(params['step'] || 1);
        if (step >= 1 && step <= 5) {
          this.mobileCheckoutStep = step;
        }
      });
  }

  onUsersChanged(value: any) {
    try {
      const raw = Number(value || 0);
      const users = Number.isFinite(raw) && raw > 0 ? raw : 0;
      // mark local pending change so server preview does not clobber client totals
      this.pendingLocalUserChange = true;
      // update form-backed summary fields for immediate UI feedback
      if (!this.subscriptionSummary) { this.subscriptionSummary = {}; }
      this.subscriptionSummary.activeUsers = users;
      this.subscriptionSummary.totalLicensedUsers = users;

      // compute client-side totals based on selected billing cycle and available prices
      const unit = this.billingCycle === 'monthly' ? (this.availableMonthlyPrice || 0) : (this.availableAnnualPrice || 0);
      const subtotal = unit * (this.isUnlimitedPlan ? 1 : users);
      const gst = Number(((subtotal || 0) * 0.18).toFixed(2));
      const grand = Number((subtotal + gst).toFixed(2));
      this.totalPrice = Number(subtotal);
      this.gstAmount = gst;
      this.grandTotal = grand;
    } catch (e) {
      console.warn('onUsersChanged error', e);
    }
  }

  onUsersBlur() {
    try {
      // user finished editing: clear pending flag and fetch authoritative preview
      this.pendingLocalUserChange = false;
      this.refreshPreview();
    } catch (e) {
      console.warn('onUsersBlur error', e);
    }
  }


  initForm() {
    this.firstFormGroup = this._formBuilder.group({
      phone: ['', [Validators.required, noWhitespaceValidator]],
      address: ['', [Validators.required, noWhitespaceValidator]],
      pincode: ['', [Validators.required, noWhitespaceValidator, Validators.pattern(/^[0-9]+$/)]],
      state: ['', [Validators.required]],
      city: ['', [Validators.required]],
      storeName: ['', [Validators.required, noWhitespaceValidator]],
      country: [''],
      pricingId: [null],
      priceMonthly: [0],
      priceAnnually: [0],
      users: ['1', [Validators.required]],
      email: ['', [Validators.required, noWhitespaceValidator, Validators.pattern(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/)]],
      txnId: [''],
      subscriptionType: [2],
      paymentGateway: ['razor']
    });
  }



  updatePrice(cycle: string) {
    const normalized = cycle.toLowerCase();
    if (normalized === 'annually' && this.availableAnnualPrice <= 0 && this.availableMonthlyPrice > 0) {
      this.billingCycle = 'monthly';
    } else if (normalized === 'monthly' && this.availableMonthlyPrice <= 0 && this.availableAnnualPrice > 0) {
      this.billingCycle = 'annually';
    } else {
      this.billingCycle = normalized;
    }
    this.refreshPreview();
  }

  /** Returns the number of days in a billing cycle — matches backend cycleDaysForBillingCycle(). */
  getCycleDays(cycle?: string): number {
    switch ((cycle || this.billingCycle || '').toUpperCase()) {
      case 'MONTHLY':     return 30;
      case 'QUARTERLY':   return 90;
      case 'HALF_YEARLY': return 180;
      default:            return 365; // ANNUALLY
    }
  }

  /** Adds the correct period to a Date based on billing cycle. Returns a new Date. */
  addCycleToDate(base: Date, cycle?: string): Date {
    const d = new Date(base);
    switch ((cycle || this.billingCycle || '').toUpperCase()) {
      case 'MONTHLY':     d.setMonth(d.getMonth() + 1); break;
      case 'QUARTERLY':   d.setMonth(d.getMonth() + 3); break;
      case 'HALF_YEARLY': d.setMonth(d.getMonth() + 6); break;
      default:            d.setFullYear(d.getFullYear() + 1); break; // ANNUALLY
    }
    return d;
  }

  /**
   * Guards a start/end date pair for safe display.
   * If start is in the future (> today), clamps it to today.
   * If end is before start, recomputes from start using the billing cycle.
   */
  sanitizeDisplayDates(start: Date, end: Date): { start: Date; end: Date } {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    let s = start && !isNaN(start.getTime()) ? start : today;
    if (s > today) s = today;
    let e = end && !isNaN(end.getTime()) ? end : this.addCycleToDate(s, this.billingCycle);
    if (e <= s) e = this.addCycleToDate(s, this.billingCycle);
    return { start: s, end: e };
  }

  loadSubscriptionState() {
    this.subscriptionsService.getSubscriptionSummary().subscribe(
      (summary: any) => {

        console.log("sas");

        this.subscriptionSummary = summary;
        this.subscriptionLoaded = true;
        // reflect subscription summary into hasActiveSubscription so template can hide purchase controls immediately
        if (this.isTruthyFlag(this.subscriptionSummary?.subscriptionActive) || this.subscriptionSummary?.subscriptionMode != 'EXPIRED') {
          this.hasActiveSubscription = true;
        }
        this.currentUsers = summary?.totalLicensedUsers || 0;
        this.billingCycle = summary?.billingCycle || this.billingCycle;
        // In plan-selection checkout flow, the target pricingId comes from the navigation state.
        // Never let the current subscription summary overwrite it with the Free/old plan ID.
        if (!this.pendingPlanSelectionState) {
          this.selectedPricingId = summary?.pricingId || this.selectedPricingId;
        }
        this.daysRemaining = Number(summary?.daysRemaining || 0);

        if (this.pendingPlanSelectionState) {
          this.selectedPricingId = Number(this.pendingPlanSelectionState?.selectedPricingId || this.selectedPricingId);
          this.billingCycle = this.pendingPlanSelectionState?.billingCycle || this.billingCycle;
          this.daysRemaining = Number(this.pendingPlanSelectionState?.daysRemaining || this.daysRemaining || 0);
        }

        if (this.subscribtionType == 2) {
          this.canRenew = this.daysRemaining <= this.renewalThresholdDays;
        }

        // ensure subscriptionSummary has derived fields so child receives updated values
        if (!this.subscriptionSummary) {
          this.subscriptionSummary = {};
        }
        this.subscriptionSummary.daysRemaining = this.daysRemaining;
        this.subscriptionSummary.billingCycle = this.billingCycle;
        // Preserve the currency symbol from the plan selection checkout flow if present.
        // Don't let the current subscription summary (e.g. Free plan in INR) overwrite the target plan's currency (e.g. Professional in USD).
        if (this.isPlanSelectionCheckoutFlow && (this.planSelectionCheckoutPreview?.currencySymbol || this.pendingPlanSelectionState?.currencySymbol)) {
          this.currencySymbol = this.planSelectionCheckoutPreview?.currencySymbol || this.pendingPlanSelectionState?.currencySymbol;
          this.currencyCode = this.planSelectionCheckoutPreview?.currencyCode || this.pendingPlanSelectionState?.currencyCode || this.currencyCode;
        } else {
          this.currencySymbol = summary.currencySymbol || this.currencySymbol;
          this.currencyCode = summary.currencyCode || this.currencyCode;
        }

        this.ensureProperSubscriptionRoute();

        this.getAccountData(() => {
          this.loadActivePricing();
          this.updateShowPurchase();
        });
      },
      () => {
        this.subscriptionLoaded = true;
        // even if summary fetch fails, still load account data so `hasActiveSubscription` can be derived
        this.getAccountData(() => {
          this.loadActivePricing();
          this.updateShowPurchase();
        });
      }
    );
  }

  private ensureProperSubscriptionRoute(): void {
    const mode = String(this.subscriptionSummary?.subscriptionMode || '').toUpperCase();
    const isTrial = mode === 'TRIAL';

    // Trial accounts on /subscription/manage → send them straight to plan-selection
    if (isTrial && this.isSubscriptionManageRoute && !this.pendingPlanSelectionState) {
      this.subscriptionsService.navigateToPlanSelection({
        source: 'renewal-component',
        currentPricingId: this.subscriptionSummary?.pricingId,
        currentBillingCycle: this.subscriptionSummary?.billingCycle,
        currentUsers: Number(this.subscriptionSummary?.activeUsers || this.subscriptionSummary?.totalLicensedUsers || 1),
        daysRemaining: this.daysRemaining,
        productId: this.productId,
        tenantId: this.subscriptionSummary?.tenantId
      });
      return;
    }

    const hasActiveSubscription = this.isTruthyFlag(this.subscriptionSummary?.subscriptionActive)
      || mode === 'PAID'
      || mode === 'ACTIVE';

    if (!hasActiveSubscription || !this.isNewRoute || this.pendingPlanSelectionState) {
      return;
    }

    this.router.navigate(['/subscription/manage'], { replaceUrl: true }).catch((error) => {
      console.warn('Failed to redirect active subscription to manage route', error);
    });
  }

  /**
   * Whether purchase controls (Products / Billing / Payment / Summary) should be shown.
   * Hide when there is an active subscription (from summary or account), unless the user is in a permitted renew flow.
   */
  canPurchase(): boolean {
    const active = this.isTruthyFlag(this.subscriptionSummary?.subscriptionActive) || this.hasActiveSubscription;
    if (this.subscribtionType == 2 && this.canRenew) {
      return true;
    }
    return !active;
  }

  /**
   * Enter renewal flow and prepare preview/payment UI.
   */
  renewNow() {
    // ensure change detection updates template reliably
    this.zone.run(() => {
      this.loadBillingAddons();
      this.loadBillingDetailsFromAccount();
      this.subscribtionType = 2;
      // recompute canRenew from daysRemaining
      this.showRenwal = true;
      this.canRenew = Number(this.daysRemaining || 0) <= this.renewalThresholdDays;
      // compute commitment term for the renewal: always start from end of current subscription period
      try {
        let start: Date;
        const nextDate = this.subscriptionSummary?.renewalDate
          || this.subscriptionSummary?.renewaldate
          || this.subscriptionSummary?.renewal_date
          || this.subscriptionSummary?.nextPaymentDate
          || this.subscriptionSummary?.next_payment_date;
        if (nextDate) {
          start = new Date(nextDate);
        } else {
          start = new Date();
        }
        if (isNaN(start.getTime())) start = new Date();
        // Do NOT reset future dates to today — that's the next cycle's start
        const end = this.addCycleToDate(start, this.billingCycle);
        const sanitized = this.sanitizeDisplayDates(start, end);
        this.startDate = sanitized.start;
        this.endDate = sanitized.end;
      } catch (e) {
        this.startDate = new Date();
        const tmp = new Date(); tmp.setFullYear(tmp.getFullYear() + 1); this.endDate = tmp;
      }
      // ensure users control and pricing are initialized
      this.initializeUsers();
      this.refreshPreview();
      // recompute visibility after entering renew flow
      this.updateShowPurchase();
      // load active pricing if not loaded
      if (!this.pricingList || this.pricingList.length === 0) {
        this.loadActivePricing();
      }
    });
  }

  loadActivePricing() {
    // Build pricingList from subscription summary — no extra API call needed.
    // pricing-preview (already called via refreshPreview) is the authoritative source.
    const summary = this.subscriptionSummary;
    const pricingId = this.selectedPricingId || summary?.pricingId;
    if (pricingId) {
      this.pricingList = [{
        pricingId,
        planName: summary?.pricingName || summary?.planName || '',
        billingCycle: this.billingCycle || summary?.billingCycle,
        priceMonthly: Number(summary?.priceMonthly || 0),
        priceAnnually: Number(summary?.priceAnnually || 0),
        perUserPrice: Number(summary?.currentUnitPrice || 0),
        // In checkout flow, unlimited must come from the target plan state, not the current summary.
        isUnlimited: this.pendingPlanSelectionState
          ? !!(this.pendingPlanSelectionState.unlimited || this.pendingPlanSelectionState.previewResponse?.unlimited || this.pendingPlanSelectionState.previewData?.unlimited)
          : this.isTruthyFlag(summary?.unlimited || summary?.isUnlimited),
        freePlan: this.isTruthyFlag(summary?.freePlan)
      }];
    }

    if (this.pendingPlanSelectionState) {
      this.selectedPricingId = Number(this.pendingPlanSelectionState?.selectedPricingId || this.selectedPricingId);
      this.billingCycle = this.pendingPlanSelectionState?.billingCycle || this.billingCycle;
      this.firstFormGroup.patchValue({
        pricingId: Number(this.pendingPlanSelectionState?.selectedPricingId || this.selectedPricingId),
        users: Number(this.pendingPlanSelectionState?.users || this.firstFormGroup.get('users')?.value || this.currentUsers || 1)
      }, { emitEvent: false });
    }

    this.updateSelectedPlan();
    this.initializeUsers();
    this.refreshPreview();
  }

  initializeUsers() {
    if (!this.shouldShowUsers()) {
      return;
    }
    const fallbackUsers = this.accountUsersFromApi != null ? this.accountUsersFromApi : this.currentUsers;
    if (this.subscribtionType == 2 || this.hasActiveSubscription || this.subscriptionSummary?.subscriptionActive) {
      this.firstFormGroup.patchValue({ users: fallbackUsers || 1 });
      return;
    }
    if (this.firstFormGroup.get('users')?.value == null) {
      this.firstFormGroup.patchValue({ users: 1 });
    }
  }

  /**
   * Decide whether to show the users input control.
   * Show when plan is not unlimited, or when backend explicitly allows ADD_USERS.
   */
  shouldShowUsers(): boolean {
    if (this.isFreePlan || this.isUnlimitedPlan) {
      return false;
    }
    const ops: any[] = this.subscriptionSummary?.allowedOperations || [];
    const allowsAdd = Array.isArray(ops) && ops.indexOf('ADD_USERS') !== -1;
    return !this.isUnlimitedPlan || allowsAdd;
  }

  onPricingChange() {
    const selected = this.firstFormGroup.get('pricingId')?.value;
    if (selected != null) {
      this.selectedPricingId = selected;
    }
    this.updateSelectedPlan();
    this.refreshPreview();
  }

  updateSelectedPlan() {
    // Find plan using a few possible id fields and coerce to string for robust matching
    let plan = this.pricingList.find((item: any) => {
      const candidateIds = [item.pricingId, item.id];
      return candidateIds.some(id => id != null && String(id) === String(this.selectedPricingId));
    });

    // fallback: try matching by numeric equality (in case selectedPricingId is number/string mismatch)
    if (!plan && this.selectedPricingId != null) {
      plan = this.pricingList.find((item: any) => {
        return item.pricingId == this.selectedPricingId;
      });
    }

    // final fallback: use first available plan
    if (!plan && this.pricingList.length > 0) {
      this.selectedPricingId = this.pricingList[0].pricingId || this.selectedPricingId;
      plan = this.pricingList[0];
    }

    if (!plan) return;

    this.plan = plan;
    if (this.selectedPricingId != null) {
      this.firstFormGroup.patchValue({ pricingId: this.selectedPricingId }, { emitEvent: false });
    }
    // In plan-selection checkout, unlimited flag was already set from the navigation state.
    // Don't overwrite it with the summary-derived plan object (which reflects current/Free plan).
    if (!this.pendingPlanSelectionState) {
      this.isUnlimitedPlan = this.isTruthyFlag(plan?.unlimited || plan?.isUnlimited);
    }
    this.availableMonthlyPrice = Number(plan?.priceMonthly || 0);
    this.availableAnnualPrice = Number(plan?.priceAnnually || 0);
    this.isFreePlan = this.isTruthyFlag(plan?.freePlan)
      || (!this.isUnlimitedPlan
          && this.availableMonthlyPrice === 0
          && this.availableAnnualPrice === 0);
    this.firstFormGroup.patchValue({
      priceMonthly: this.availableMonthlyPrice,
      priceAnnually: this.availableAnnualPrice
    }, { emitEvent: false });
    if (this.availableAnnualPrice <= 0 && this.availableMonthlyPrice > 0) {
      this.billingCycle = 'monthly';
    } else if (this.availableMonthlyPrice <= 0 && this.availableAnnualPrice > 0) {
      this.billingCycle = 'annually';
    }
    if (this.billingCycle === 'monthly' && this.availableMonthlyPrice > 0) {
      this.displayPlanPrice = this.availableMonthlyPrice;
    } else if (this.billingCycle === 'annually' && this.availableAnnualPrice > 0) {
      this.displayPlanPrice = this.availableAnnualPrice;
    }

    const usersControl = this.firstFormGroup.get('users');
    if (usersControl && (usersControl.value == null || usersControl.value === '')) {
      usersControl.patchValue(1, { emitEvent: false });
    }

    // patch billingCycle back into subscriptionSummary so child updates immediately
    if (!this.subscriptionSummary) { this.subscriptionSummary = {}; }
    this.subscriptionSummary.billingCycle = this.billingCycle;
  }

  isTruthyFlag(value: any): boolean {
    if (value === true || value === 1) {
      return true;
    }
    const normalized = String(value || '').toLowerCase();
    return normalized === '1' || normalized === 'true' || normalized === 'yes';
  }

  resolveOperationType(): string {
    if (this.forcedOperationType) {
      return this.forcedOperationType;
    }
    if (this.pendingAddonOnly) {
      return 'ADDON_NEW';
    }
    if (this.subscribtionType == 2) {
      return 'RENEW_SUBSCRIPTION';
    }
    if (this.isNewRoute) {
      return 'NEW_SUBSCRIPTION';
    }
    if (this.subscriptionSummary?.subscriptionActive || this.hasActiveSubscription) {
      return 'ADD_USERS';
    }
    if (this.subscriptionSummary?.trialActive) {
      return 'TRIAL_CONVERSION';
    }
    return 'DIRECT_SUBSCRIPTION';
  }

  refreshPreview() {
    if (!this.selectedPricingId) {
      return;
    }
    if (this.isFreePlan || this.isUnlimitedPlan) {
      this.pricingPreview = null;
      this.isPreviewLoading = false;
      return;
    }
    const usersValue = Number(this.firstFormGroup.get('users')?.value || 0);
    const operationType = this.resolveOperationType();
    const effectiveUsers = this.isUnlimitedPlan ? 1 : usersValue;
    const additionalUsers = Math.max(0, effectiveUsers - this.currentUsers);

    const payload: any = {
      operationType: operationType,
      pricingId: this.selectedPricingId,
      billingCycle: this.billingCycle,
      users: effectiveUsers
    };

    if (operationType === 'ADD_USERS') {
      payload.additionalUsers = additionalUsers;
    }

    if (operationType === 'UPGRADE_PLAN') {
      payload.currentPricingId = this.pendingPlanSelectionState?.currentPricingId || this.subscriptionSummary?.pricingId;
      payload.currentUsers = this.pendingPlanSelectionState?.currentUsers || this.currentUsers;
      payload.daysRemaining = this.pendingPlanSelectionState?.daysRemaining || this.daysRemaining;
    }

    this.isPreviewLoading = true;
    this.subscriptionsService.getPricingPreview(payload).subscribe(
      (preview: any) => {
        this.isPreviewLoading = false;
        if (preview?.error && typeof preview.error === 'string' && preview.error.toLowerCase().includes('unlimited')) {
          this.isUnlimitedPlan = true;
          if (this.plan) this.plan.isUnlimited = true;
          this.pricingList.forEach((p: any) => p.isUnlimited = true);
        }
        this.applyPreview(this.mergePlanSelectionPreview(preview));
      },
      (err: any) => {
        this.isPreviewLoading = false;
        const errMsg = err?.error?.error || err?.error?.message || (typeof err?.error === 'string' ? err.error : '');
        if (errMsg && errMsg.toLowerCase().includes('unlimited')) {
          this.isUnlimitedPlan = true;
          if (this.plan) this.plan.isUnlimited = true;
          this.pricingList.forEach((p: any) => p.isUnlimited = true);
        }
      }
    );
  }

  applyPreview(preview: any) {
    if (!preview || preview.error) {
      return;
    }
    this.pricingPreview = preview;
    this.currencySymbol = preview.currencySymbol || this.currencySymbol;
    this.currencyCode = preview.currencyCode || this.currencyCode;
    // If user is actively editing users locally, avoid overwriting client-side totals
    if (this.pendingLocalUserChange) {
      // still update available prices and preview object, but preserve local totals/users
      const monthly = Number(preview.priceMonthly || 0);
      const annually = Number(preview.priceAnnually || 0);
      if (monthly > 0) { this.availableMonthlyPrice = monthly; }
      if (annually > 0) { this.availableAnnualPrice = annually; }
      this.firstFormGroup.patchValue({
        priceMonthly: this.availableMonthlyPrice,
        priceAnnually: this.availableAnnualPrice
      }, { emitEvent: false });
      // Merge other preview fields but keep totals from client
      try {
        const previewSummary: any = {
          nextPaymentAmount: this.grandTotal || this.subscriptionSummary?.nextPaymentAmount,
          nextPaymentDate: preview.nextPaymentDate || preview.projectedValidityDate || preview.cycleEndDate || preview.cycleStartDate || this.subscriptionSummary?.nextPaymentDate || this.subscriptionSummary?.renewalDate,
          subtotal: this.totalPrice || this.subscriptionSummary?.subtotal,
          gstAmount: this.gstAmount || this.subscriptionSummary?.gstAmount,
          grandTotal: this.grandTotal || this.subscriptionSummary?.grandTotal,
          netTotal: this.totalPrice || this.subscriptionSummary?.netTotal,
          priceMonthly: this.availableMonthlyPrice,
          priceAnnually: this.availableAnnualPrice,
          daysRemaining: this.subscriptionSummary?.daysRemaining || this.daysRemaining
        };
        this.subscriptionSummary = Object.assign({}, this.subscriptionSummary || {}, previewSummary);
      } catch (e) {
        console.warn('Failed to merge preview into subscriptionSummary (local edit)', e);
      }
      return;
    }

    this.totalPrice = Number(preview.subtotal || 0);
    this.gstAmount = Number(preview.gstAmount || 0);
    this.grandTotal = Number(preview.grandTotal || 0);
    this.isUnlimitedPlan = this.isTruthyFlag(preview.unlimited || preview.isUnlimited);
    const monthly = Number(preview.priceMonthly || 0);
    const annually = Number(preview.priceAnnually || 0);
    if (monthly > 0) {
      this.availableMonthlyPrice = monthly;
    }
    if (annually > 0) {
      this.availableAnnualPrice = annually;
    }
    this.firstFormGroup.patchValue({
      priceMonthly: this.availableMonthlyPrice,
      priceAnnually: this.availableAnnualPrice
    }, { emitEvent: false });
    if (annually <= 0 && monthly > 0) {
      this.billingCycle = 'monthly';
    } else if (monthly <= 0 && annually > 0) {
      this.billingCycle = 'annually';
    }
    if (this.billingCycle === 'monthly' && monthly > 0) {
      this.displayPlanPrice = monthly;
    } else if (this.billingCycle === 'annually' && annually > 0) {
      this.displayPlanPrice = annually;
    } else {
      this.displayPlanPrice = Number(preview.planPricePerUser || 0);
    }
    const opType = String(preview.operationType || '').toUpperCase();
    // For trial conversions and new subscriptions, always use today as start date.
    // TRIAL_CONVERSION always uses today regardless of subscribtionType (backend may normalize
    // RENEW_SUBSCRIPTION → TRIAL_CONVERSION when subscription is inactive but trial is active).
    const isTrial = this.subscriptionSummary?.trialActive
      || String(this.subscriptionSummary?.subscriptionMode || '').toUpperCase() === 'TRIAL';
    const useCurrentCycle = opType === 'TRIAL_CONVERSION'
      || isTrial
      || ((opType === 'NEW_SUBSCRIPTION' || opType === 'DIRECT_SUBSCRIPTION')
          && (this.subscribtionType != 2 || this.isNewSubscriptionFromPlanSelection));

    if (useCurrentCycle) {
      const today = new Date();
      const next = this.addCycleToDate(today, this.billingCycle);
      this.startDate = today;
      this.endDate = next;
    } else {
      // For renew flow: start from the end of the current subscription period
      if (this.subscribtionType == 2) {
        const renewalDateStr = this.subscriptionSummary?.renewalDate
          || this.subscriptionSummary?.renewaldate
          || this.subscriptionSummary?.renewal_date
          || this.subscriptionSummary?.cycleEndDate
          || preview.currentRenewalDate
          || preview.cycleEndDate;
        const renewalDate = renewalDateStr ? new Date(renewalDateStr) : null;
        if (renewalDate && !isNaN(renewalDate.getTime())) {
          this.startDate = renewalDate;
          const nextEnd = this.addCycleToDate(renewalDate, this.billingCycle);
          this.endDate = nextEnd;
        } else if (preview.cycleStartDate) {
          this.startDate = new Date(preview.cycleStartDate);
          if (preview.cycleEndDate) {
            const sanitized = this.sanitizeDisplayDates(this.startDate, new Date(preview.cycleEndDate));
            this.startDate = sanitized.start;
            this.endDate = sanitized.end;
          }
        }
      } else {
        if (preview.cycleStartDate) {
          this.startDate = new Date(preview.cycleStartDate);
        }
        if (preview.cycleEndDate) {
          const sanitized = this.sanitizeDisplayDates(this.startDate, new Date(preview.cycleEndDate));
          this.startDate = sanitized.start;
          this.endDate = sanitized.end;
        }
      }
    }

    // ensure preview-derived billingCycle is visible to child
    if (!this.subscriptionSummary) { this.subscriptionSummary = {}; }
    this.subscriptionSummary.billingCycle = this.billingCycle;
    // Merge important preview fields into subscriptionSummary so UI shows accurate previewed values
    try {
      const previewSummary: any = {
        nextPaymentAmount: preview.grandTotal || preview.nextPaymentAmount || preview.amount || this.subscriptionSummary.nextPaymentAmount || this.grandTotal,
        nextPaymentDate: preview.nextPaymentDate || preview.projectedValidityDate || preview.cycleEndDate || preview.cycleStartDate || this.subscriptionSummary.nextPaymentDate || this.subscriptionSummary.renewalDate,
        subtotal: preview.subtotal != null ? Number(preview.subtotal) : (this.totalPrice || this.subscriptionSummary.subtotal),
        gstAmount: preview.gstAmount != null ? Number(preview.gstAmount) : (this.gstAmount || this.subscriptionSummary.gstAmount),
        grandTotal: preview.grandTotal != null ? Number(preview.grandTotal) : (this.grandTotal || this.subscriptionSummary.grandTotal),
        netTotal: preview.netTotal || preview.subtotal || this.totalPrice || this.subscriptionSummary.netTotal,
        priceMonthly: preview.priceMonthly || this.availableMonthlyPrice,
        priceAnnually: preview.priceAnnually || this.availableAnnualPrice,
        daysRemaining: this.subscriptionSummary.daysRemaining || this.daysRemaining
      };
      this.subscriptionSummary = Object.assign({}, this.subscriptionSummary || {}, previewSummary);
    } catch (e) {
      // non-fatal: keep existing subscriptionSummary
      console.warn('Failed to merge preview into subscriptionSummary', e);
    }
  }




  //country
  countryList: any;
  countryTypeList: any = [];
  filteredcountryTypeList: any = [];
  countryControl: UntypedFormControl = new UntypedFormControl();
  countryFilterControl: UntypedFormControl = new UntypedFormControl();
  options: any;

  // protected _onDestroy = new Subject<void>();


  getCountryList() {
    this.commonService.getCountryList().subscribe(
      data => {
        this.countryList = data;
        this.countryTypeList = data;
        this.filteredcountryTypeList = this.countryTypeList;
      }
    )
  }

  // Country, State, City Methods for Location API

  /**
   * Load all countries on component init
   */
  loadLocationCountries() {
    this.countryStateCityService.getAllCountries()
      .pipe(takeUntil(this._onDestroy))
      .subscribe(
        res => {
          this.locationCountries = res;
          this.filteredLocationCountries = res;
          this.autoDetectAndPatchCountry();
        },
        error => {
          console.error('Error loading countries', error);
        }
      );
  }

  /**
   * Auto-patch the Country field using stored IP geolocation data.
   * Only patches if the country field is currently empty.
   */
  autoDetectAndPatchCountry() {
    const countryControl = this.firstFormGroup?.get('country');
    if (!countryControl) {
      return;
    }

    let raw = localStorage.getItem('CL');
    if (!raw) {
      this.countryLocationService.getCountryCode();
      raw = localStorage.getItem('CL');
    }
    if (!raw) { return; }

    try {
      const geo = JSON.parse(raw);

      // 1. Match and patch Country
      if (!countryControl.value) {
        const iso2 = (geo.country_code || geo.country || '').toUpperCase();
        if (iso2) {
          const matched = this.locationCountries.find(
            (c: any) => (c.iso2 || '').toUpperCase() === iso2
          );
          if (matched) {
            countryControl.setValue(matched.name, { emitEvent: false });
            this.selectedCountryId = matched.id || matched;

            // Set payment gateway based on country
            if (matched.name.toLowerCase() === 'india') {
              this.firstFormGroup.patchValue({ paymentGateway: 'razor' });
            } else {
              this.firstFormGroup.patchValue({ paymentGateway: 'stripe' });
            }

            // 2. Load states and then patch state/city sequentially
            this.countryStateCityService.getStatesByCountry(this.selectedCountryId)
              .pipe(takeUntil(this._onDestroy))
              .subscribe(states => {
                this.locationStates = states;
                this.filteredLocationStates = states;

                const stateControl = this.firstFormGroup?.get('state');
                if (stateControl && !stateControl.value && geo.region) {
                  const regionName = String(geo.region).trim().toLowerCase();
                  const matchedState = this.locationStates.find(
                    (s: any) => String(s.name || '').trim().toLowerCase() === regionName
                  );
                  if (matchedState) {
                    stateControl.setValue(matchedState.name, { emitEvent: false });
                    this.selectedStateId = matchedState.id || matchedState;

                    // 3. Load cities and patch city
                    this.countryStateCityService.getCitiesByState(this.selectedCountryId, this.selectedStateId)
                      .pipe(takeUntil(this._onDestroy))
                      .subscribe(cities => {
                        this.locationCities = cities;
                        this.filteredLocationCities = cities;

                        const cityControl = this.firstFormGroup?.get('city');
                        if (cityControl && !cityControl.value && geo.city) {
                          const cityName = String(geo.city).trim().toLowerCase();
                          const matchedCity = this.locationCities.find(
                            (c: any) => String(c.name || '').trim().toLowerCase() === cityName
                          );
                          if (matchedCity) {
                            cityControl.setValue(matchedCity.name, { emitEvent: false });
                            this.selectedCityId = matchedCity.id || matchedCity;
                          } else {
                            cityControl.setValue(geo.city, { emitEvent: false });
                          }
                        }
                      });
                  } else {
                    stateControl.setValue(geo.region, { emitEvent: false });
                    const cityControl = this.firstFormGroup?.get('city');
                    if (cityControl && !cityControl.value && geo.city) {
                      cityControl.setValue(geo.city, { emitEvent: false });
                    }
                  }
                }
              });
          }
        }
      }
    } catch (e) {
      console.warn('autoDetectAndPatchCountry: failed to parse geo data', e);
    }
  }

  /**
   * Handle country search input
   */
  onLocationCountrySearch() {
    this.countrySearchControl.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe((searchText: string) => {
        if (!searchText) {
          this.filteredLocationCountries = this.locationCountries;
        } else {
          const lowerText = searchText.toLowerCase();
          this.filteredLocationCountries = this.locationCountries.filter(c =>
            c.name?.toLowerCase().includes(lowerText)
          );
        }
      });
  }

  /**
   * Handle country selection change
   */
  onLocationCountryChange(event: any) {
    const selectedCountry = event.value;
    if (selectedCountry) {
      // Find the full country object by name (case-insensitive)
      const countryObj = this.locationCountries.find(c => c.name?.toLowerCase() === selectedCountry?.toLowerCase());
      this.selectedCountryId = countryObj ? (countryObj.id || countryObj) : selectedCountry;

      // Reset state and city
      this.locationStates = [];
      this.locationCities = [];
      this.filteredLocationStates = [];
      this.filteredLocationCities = [];
      this.selectedStateId = null;
      this.selectedCityId = null;

      // Clear state and city form controls
      const stateControl = this.firstFormGroup.get('state');
      const cityControl = this.firstFormGroup.get('city');
      if (stateControl) stateControl.reset();
      if (cityControl) cityControl.reset();

      // Update payment gateway based on country and currency
      if (selectedCountry.toLowerCase() === 'india' && this.currency === 'INR') {
        this.firstFormGroup.patchValue({ paymentGateway: 'razor' });
      } else {
        this.firstFormGroup.patchValue({ paymentGateway: 'stripe' });
      }

      // Load states for selected country
      this.loadLocationStates(this.selectedCountryId);
    }
  }

  /**
   * Load states for selected country
   */
  loadLocationStates(countryId: any) {
    this.countryStateCityService.getStatesByCountry(countryId)
      .pipe(takeUntil(this._onDestroy))
      .subscribe(
        res => {
          this.locationStates = res;
          this.filteredLocationStates = res;
          this.syncStateSelectionFromForm();
        },
        error => {
          console.error('Error loading states', error);
        }
      );
  }

  /**
   * Handle state search input
   */
  onLocationStateSearch() {
    this.stateSearchControl.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe((searchText: string) => {
        if (!searchText) {
          this.filteredLocationStates = this.locationStates;
        } else {
          const lowerText = searchText.toLowerCase();
          this.filteredLocationStates = this.locationStates.filter(s =>
            s.name?.toLowerCase().includes(lowerText)
          );
        }
      });
  }

  /**
   * Handle state selection change
   */
  onLocationStateChange(event: any) {
    const selectedState = event.value;
    if (selectedState && this.selectedCountryId) {
      // Find the full state object by name (case-insensitive)
      const stateObj = this.locationStates.find(s => s.name?.toLowerCase() === selectedState?.toLowerCase());
      this.selectedStateId = stateObj ? (stateObj.id || stateObj) : selectedState;

      // Reset city
      this.locationCities = [];
      this.filteredLocationCities = [];
      this.selectedCityId = null;

      // Clear city form control
      const cityControl = this.firstFormGroup.get('city');
      if (cityControl) cityControl.reset();

      // Load cities for selected state
      this.loadLocationCities(this.selectedCountryId, this.selectedStateId);
    }
  }

  /**
   * Load cities for selected state
   */
  loadLocationCities(countryId: any, stateId: any) {
    this.countryStateCityService.getCitiesByState(countryId, stateId)
      .pipe(takeUntil(this._onDestroy))
      .subscribe(
        res => {
          this.locationCities = res;
          this.filteredLocationCities = res;
          this.syncCitySelectionFromForm();
        },
        error => {
          console.error('Error loading cities', error);
        }
      );
  }

  private syncLocationSelectionsFromForm(): void {
    const countryValue = this.firstFormGroup?.get('country')?.value;
    if (!countryValue || !this.locationCountries.length) {
      return;
    }

    const countryObj = this.findLocationOption(this.locationCountries, countryValue);
    if (!countryObj) {
      return;
    }

    this.selectedCountryId = countryObj.id || countryObj;
    if (countryObj.name && countryObj.name !== countryValue) {
      this.firstFormGroup.patchValue({ country: countryObj.name }, { emitEvent: false });
    }

    const stateValue = this.firstFormGroup?.get('state')?.value;
    const cityValue = this.firstFormGroup?.get('city')?.value;
    if (stateValue && !this.locationStates.length) {
      this.loadLocationStates(this.selectedCountryId);
      return;
    }

    if (stateValue) {
      this.syncStateSelectionFromForm();
      if (cityValue && this.selectedStateId && !this.locationCities.length) {
        this.loadLocationCities(this.selectedCountryId, this.selectedStateId);
      }
    }
  }

  private syncStateSelectionFromForm(): void {
    const stateValue = this.firstFormGroup?.get('state')?.value;
    if (!stateValue || !this.locationStates.length) {
      return;
    }

    const stateObj = this.findLocationOption(this.locationStates, stateValue);
    if (!stateObj) {
      return;
    }

    this.selectedStateId = stateObj.id || stateObj;
    if (stateObj.name && stateObj.name !== stateValue) {
      this.firstFormGroup.patchValue({ state: stateObj.name }, { emitEvent: false });
    }

    const cityValue = this.firstFormGroup?.get('city')?.value;
    if (cityValue && !this.locationCities.length && this.selectedCountryId && this.selectedStateId) {
      this.loadLocationCities(this.selectedCountryId, this.selectedStateId);
      return;
    }

    if (cityValue) {
      this.syncCitySelectionFromForm();
    }
  }

  private syncCitySelectionFromForm(): void {
    const cityValue = this.firstFormGroup?.get('city')?.value;
    if (!cityValue || !this.locationCities.length) {
      return;
    }

    const cityObj = this.findLocationOption(this.locationCities, cityValue);
    if (!cityObj) {
      return;
    }

    this.selectedCityId = cityObj.id || cityObj;
    if (cityObj.name && cityObj.name !== cityValue) {
      this.firstFormGroup.patchValue({ city: cityObj.name }, { emitEvent: false });
    }
  }

  private findLocationOption(options: any[], value: any): any | null {
    const normalizedValue = String(value || '').trim().toLowerCase();
    if (!normalizedValue) {
      return null;
    }

    return options.find((option: any) => {
      const optionName = String(option?.name || '').trim().toLowerCase();
      const optionId = String(option?.id || '').trim().toLowerCase();
      return optionName === normalizedValue || optionId === normalizedValue;
    }) || null;
  }

  /**
   * Handle city search input
   */
  onLocationCitySearch() {
    this.citySearchControl.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe((searchText: string) => {
        if (!searchText) {
          this.filteredLocationCities = this.locationCities;
        } else {
          const lowerText = searchText.toLowerCase();
          this.filteredLocationCities = this.locationCities.filter(c =>
            c.name?.toLowerCase().includes(lowerText)
          );
        }
      });
  }

  /**
   * Handle city selection change
   */
  onLocationCityChange(event: any) {
    const selectedCity = event.value;
    if (selectedCity) {
      // Find the full city object by name (case-insensitive)
      const cityObj = this.locationCities.find(c => c.name?.toLowerCase() === selectedCity?.toLowerCase());
      this.selectedCityId = cityObj ? (cityObj.id || cityObj) : selectedCity;
    }
  }

  getAllCountry() {
    this.filteredcountryTypeList = this.countryTypeList;
  }

  countrYFilter() {
    this.countryFilterControl.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe(() => {
        this.countryFilter();
      });
  }

  protected countryFilter() {
    if (!this.countryTypeList) {
      return;
    }
    // get the search keyword
    let search = this.countryFilterControl.value;
    if (!search) {
      this.filteredcountryTypeList = this.countryTypeList.slice();
      return;
    } else {
      search = search.toLowerCase();
    }
    // filter the banks
    this.filteredcountryTypeList = this.countryTypeList.filter((country: any) => country['countryName'].toLowerCase().indexOf(search) > -1)
  }




  contact: any = {};




  paymentResponceRazorPAy(i: any) {

    console.log(i, "payments");


    this.loaderService.show();
    let orderTranscationId = i.salesPaymentTransaction[0].txn_id;
    let additionalData = i.salesFlatOrderPayment.additionalInformation
    let mode = i.salesFlatOrderPayment.method === 'Stripe' ? 'stripe' : 'razor_pay';

    let formUsers = Number(this.firstFormGroup.value.users || 0);
    // When renewing/paying for an existing subscription the form may still hold
    // the default value of 1 (never patched before payment).  Prefer the known
    // subscription user count so the payment record stores the correct value.
    const knownUsers = Number(
      this.currentUsers
      || this.subscriptionSummary?.totalLicensedUsers
      || this.subscriptionSummary?.activeUsers
      || 0
    );
    let usersValue = formUsers > 0 ? formUsers : (knownUsers > 0 ? knownUsers : 1);
    let operationType = this.resolveOperationType();
    const preserveUpgradeSelection = this.isPlanSelectionCheckoutFlow;
    const checkoutPricingId = preserveUpgradeSelection
      ? Number(this.pendingPlanSelectionState?.selectedPricingId || this.planSelectionCheckoutPreview?.pricingId || this.selectedPricingId || 0)
      : Number(this.selectedPricingId || 0);
    const checkoutBillingCycle = preserveUpgradeSelection
      ? String(this.pendingPlanSelectionState?.billingCycle || this.billingCycle || 'monthly').toUpperCase()
      : String(this.billingCycle || 'monthly').toUpperCase();
    // If parent was requested to take payment for add-users, prefer that
    if (this.pendingAddUsers && Number(this.pendingAddUsers) > 0) {
      operationType = 'ADD_USERS';
      const current = Number(this.subscriptionSummary?.activeUsers || this.currentUsers || 0);
      const effective = this.isUnlimitedPlan ? 1 : (current + Number(this.pendingAddUsers));
      usersValue = effective;
    }
    const effectiveUsers = this.isUnlimitedPlan ? 1 : usersValue;
    const additionalUsers = this.pendingAddUsers && Number(this.pendingAddUsers) > 0 ? Number(this.pendingAddUsers) : Math.max(0, effectiveUsers - this.currentUsers);
    const checkoutContactFields = this.buildCheckoutContactFields();

    let object: any = {
      operationType: operationType,
      pricingId: checkoutPricingId,
      billingCycle: checkoutBillingCycle,
      users: effectiveUsers,
      additionalUsers: additionalUsers,
      mode: mode,
      txnId: this.firstFormGroup.value.txnId,
      paymentStatus: i.paypalPaymentStatus === 'Successful' ? 'Paid' : i.paypalPaymentStatus,
      phone: this.firstFormGroup.value.phone,
      email: this.firstFormGroup.value.email,
      name: checkoutContactFields.name,
      country: checkoutContactFields.country,
      address: checkoutContactFields.address,
      city: checkoutContactFields.city,
      state: checkoutContactFields.state,
      pincode: this.firstFormGroup.value.pincode,
      productInfo: 'Crm',
      customValue: additionalData,
      transactionId: orderTranscationId,
      paymentModeType: this.firstFormGroup.value.paymentGateway,
      couponCode: this.appliedCoupon?.couponCode || null,
      couponDiscountAmount: this.couponDiscount > 0 ? this.couponDiscount : null
    }

    if (operationType === 'UPGRADE_PLAN' || operationType === 'RENEW_SUBSCRIPTION' || operationType === 'NEW_SUBSCRIPTION' || operationType === 'TRIAL_CONVERSION') {
      object = Object.assign(object, this.buildPlanSelectionCheckoutPayload(operationType));
    }

    if (operationType === 'SAME_PLAN_BILLING_CHANGE') {
      object = Object.assign(object, this.buildPlanSelectionCheckoutPayload(operationType));
    }

    // Attach any new addons selected on the billing screen
    if (this.billingAddonSelections.length > 0) {
      object.newAddons = this.billingAddonSelections.map(s => ({
        addonCode: s.addon.addonCode,
        addonType: s.addon.addonType,   // required for backend PLAN-type sync check
        quantity: s.quantity
      }));
    }


    let object1 = {
      "name": this.firstFormGroup.value.name,
      "contact": this.firstFormGroup.value.phoneNumber,
      "email": this.firstFormGroup.value.email,
    }

    this.contact = object1;


    if (object != null) {
      // clear pendingAddUsers after constructing payload
      this.pendingAddUsers = 0;
      const dateError = this.validateSubscriptionDates(object);
      if (dateError) {
        this.loaderService.hide();
        this.snackBar.open(dateError, '×', { panelClass: 'error', verticalPosition: 'bottom', duration: 5000 });
        return;
      }
      this.makePayment(object);
    }

  }

  private buildCheckoutContactFields(): { name: string; country: string; address: string; city: string; state: string } {
    return {
      name: this.normalizeCheckoutText(this.firstFormGroup.value.storeName),
      country: this.normalizeCheckoutText(this.firstFormGroup.value.country),
      address: this.normalizeCheckoutText(this.firstFormGroup.value.address),
      city: this.normalizeCheckoutText(this.firstFormGroup.value.city),
      state: this.normalizeCheckoutText(this.firstFormGroup.value.state)
    };
  }

  private normalizeCheckoutText(value: any): string {
    const normalized = String(value || '')
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^\x20-\x7E]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    return normalized;
  }

  private buildPlanSelectionCheckoutPayload(operationType: string): any {
    const preview = this.normalizePlanSelectionPreview(
      this.planSelectionCheckoutPreview
      || this.pricingPreview
      || this.pendingPlanSelectionState?.previewResponse
      || this.pendingPlanSelectionState?.previewData
      || {}
    ) || {};

    const finalUsers = Number(
      this.pendingPlanSelectionState?.users
      || this.firstFormGroup?.get('users')?.value
      || this.currentUsers
      || 1
    ) || 1;
    const currentUsers = Number(
      this.pendingPlanSelectionState?.currentUsers
      || this.currentUsers
      || this.subscriptionSummary?.activeUsers
      || 0
    ) || 0;
    const subtotal = preview.subtotal != null ? Number(preview.subtotal) : Number(preview.payable || this.computedSubtotal || 0);
    const gstAmount = preview.gstAmount != null ? Number(preview.gstAmount) : Number(this.computedGst || 0);
    const grandTotal = preview.grandTotal != null ? Number(preview.grandTotal) : Number(preview.finalPayable || this.computedGrandTotal || 0);
    const planPricePerUser = finalUsers > 0 ? Number((subtotal / finalUsers).toFixed(2)) : subtotal;
    const baseUsers = operationType === 'NEW_SUBSCRIPTION' ? 1 : Math.max(1, Math.min(currentUsers || finalUsers, finalUsers));
    const additionalUsers = Math.max(0, finalUsers - baseUsers);
    const currentValidityDate = preview.currentValidityDate || preview.currentValidTill || null;
    const projectedValidityDate = preview.projectedValidityDate || preview.projectedValidTill || null;
    // Trial accounts always start today — detect trial regardless of what operationType was passed
    // (e.g. RENEW_SUBSCRIPTION from subscribtionType==2 gets normalized to TRIAL_CONVERSION by backend)
    const isTrial = this.subscriptionSummary?.trialActive
      || String(this.subscriptionSummary?.subscriptionMode || '').toUpperCase() === 'TRIAL';
    const isFreshStart = operationType === 'NEW_SUBSCRIPTION'
      || operationType === 'TRIAL_CONVERSION'
      || operationType === 'DIRECT_SUBSCRIPTION'
      || isTrial;
    const cycleStartDate = isFreshStart
      ? new Date().toISOString()
      : (currentValidityDate || new Date().toISOString());

    return {
      currentPricingId: Number(this.pendingPlanSelectionState?.currentPricingId || this.subscriptionSummary?.pricingId || 0),
      currentUsers: currentUsers,
      daysRemaining: Number(this.pendingPlanSelectionState?.daysRemaining || this.daysRemaining || 0),
      currentBillingCycle: String(this.pendingPlanSelectionState?.currentBillingCycle || this.subscriptionSummary?.billingCycle || this.billingCycle || 'MONTHLY').toUpperCase(),
      currentPlanName: this.upgradeCurrentPlanName,
      targetPlan: this.upgradeTargetPlanName,
      selectedPlanName: this.upgradeTargetPlanName,
      pricingNameSnapshot: this.upgradeTargetPlanName,
      currentPrice: preview.currentPrice ?? null,
      targetPrice: preview.targetPrice ?? subtotal,
      unusedAmount: preview.unusedAmount ?? null,
      payable: subtotal,
      subtotal: subtotal,
      totalPrice: subtotal,
      gstAmount: gstAmount,
      taxAmount: gstAmount,
      finalPayable: grandTotal,
      grandTotal: grandTotal,
      amount: grandTotal,
      currentValidTill: currentValidityDate,
      projectedValidTill: projectedValidityDate,
      message: preview.message ?? null,
      baseUsers: baseUsers,
      finalUsers: finalUsers,
      additionalUsers: additionalUsers,
      planPricePerUser: planPricePerUser,
      subscriptionStartDate: cycleStartDate,
      subscriptionEndDate: projectedValidityDate,
      renewalDate: projectedValidityDate,
      cycleStartDate: cycleStartDate,
      cycleEndDate: projectedValidityDate,
      prorationDays: Number(preview.remainingDays || 0),
      cycleDays: Number(preview.totalDays || 0)
    };
  }

  /**
   * Validates subscription date fields in a checkout payload.
   * Returns an error message string if invalid, or null if valid.
   * Also auto-corrects obvious errors (future start dates, inverted ranges) with a warning.
   */
  private validateSubscriptionDates(payload: any): string | null {
    const op = String(payload.operationType || '').toUpperCase();
    const isAddUsers = op === 'ADD_USERS' || op === 'MANUAL_ADD_USERS';
    if (isAddUsers) return null; // date rules don't apply to add-user ops

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const parseDate = (v: any): Date | null => {
      if (!v) return null;
      const d = new Date(v);
      return isNaN(d.getTime()) ? null : d;
    };

    const startDate = parseDate(payload.subscriptionStartDate || payload.cycleStartDate);
    const endDate = parseDate(payload.subscriptionEndDate || payload.cycleEndDate || payload.renewalDate);

    const isFreshStart = ['NEW_SUBSCRIPTION', 'TRIAL_CONVERSION', 'DIRECT_SUBSCRIPTION', 'MANUAL_SUBSCRIPTION'].includes(op);

    // Rule 1: start date must not be in the future for fresh starts
    if (isFreshStart && startDate && startDate > today) {
      // Auto-correct: clamp to today
      const todayIso = new Date().toISOString();
      payload.subscriptionStartDate = todayIso;
      payload.cycleStartDate = todayIso;
      this.snackBar.open('Start date was in the future — corrected to today.', '×',
        { panelClass: 'warn', verticalPosition: 'bottom', duration: 4000 });
    }

    // Re-read after potential correction
    const effectiveStart = parseDate(payload.subscriptionStartDate || payload.cycleStartDate) || today;

    // Rule 2: end date must be after start date
    if (endDate && endDate < effectiveStart) {
      return 'End date cannot be earlier than start date. Please refresh and try again.';
    }

    // Rule 3: end date must be in the future
    if (endDate && endDate <= today) {
      return 'Subscription end date must be in the future. Please refresh and try again.';
    }

    return null;
  }

  makePayment(object: any) {

    this.zone.run(() => {



      let refId = this._activatedRoute.snapshot.queryParamMap.get("refId");
      if (refId != null) {


        object['referralId'] = refId;

      }

      this.subscriptionsService.checkoutSubscription(object).subscribe(
        (response: any) => {
          this.loaderService.hide();
          if (response['status_code'] == Constant.RESPONSE_SUCCESS) {
            this.clearTrialRestrictionCache();

            this.commonService.show();

            this.commonService.updateText('Add New User');


            this.commonService.hide();
            this.snackBar.open(response['status_message'], '×', { panelClass: 'success', verticalPosition: 'bottom', duration: 3000 });

            // For renewal/upgrade flows the backend rebuilds Redis permission + menu caches.
            // A hard reload is required so the browser fetches fresh menus instead of
            // serving the cached Angular route with stale menu data.
            const isRenewalOrUpgrade = object?.operationType === 'RENEW_SUBSCRIPTION'
              || object?.operationType === 'UPGRADE_PLAN'
              || object?.operationType === 'SAME_PLAN_BILLING_CHANGE'
              || this.subscribtionType == 2;
            if (isRenewalOrUpgrade) {
              window.location.href = '/dashboard';
            } else {
              this.router.navigate(['/dashboard']);
            }



          }
          else {

            this.snackBar.open(response['status_message'], '×', { panelClass: 'error', verticalPosition: 'bottom', duration: 3000 });
          }
        },
        (err) => { });

    });

  }

  private clearTrialRestrictionCache(): void {
    try {
      localStorage.removeItem('trialExpiredMessage');
      localStorage.removeItem('trialExpiryData');
    } catch (error) {
      console.warn('Failed to clear trial restriction cache after checkout', error);
    }
  }

  computeAddUserCharge() {
    // Compute a simple prorated charge for additional users based on remaining days
    const users = Number(this.addUserCount || 0);
    const daysRem = Number(this.daysRemaining || this.subscriptionSummary?.daysRemaining || 0);
    // Determine unit monthly price (prefer monthly pricing; fallback to annual/12)
    let unitMonthly = 0;
    if (this.availableMonthlyPrice && this.availableMonthlyPrice > 0) unitMonthly = this.availableMonthlyPrice;
    else if (this.availableAnnualPrice && this.availableAnnualPrice > 0) unitMonthly = this.availableAnnualPrice / 12;
    // fraction of month remaining (approximate month = 30d)
    const frac = Math.max(0, Math.min(1, daysRem / 30));
    const base = unitMonthly * users * frac;
    const tax = base * 0.18; // GST 18%
    // keep addUserCharge as amount excluding GST and addUserTax as GST
    this.addUserCharge = Math.round(base * 100) / 100;
    this.addUserTax = Math.round(tax * 100) / 100;
  }

  cancelAddUsers() {
    this.addUser = false;
    this.addUserCount = 1;
    this.addUserCharge = 0;
    this.addUserTax = 0;
  }

  // Handle payment response from in-modal payment widget
  handleAddUserPayment(event: any) {
    // Event shape mirrors paymentResponceRazorPAy; attempt to extract txn id
    try {
      const salesPayment = (event && event.salesPaymentTransaction && event.salesPaymentTransaction[0]) || {};
      const orderTranscationId = salesPayment.txn_id || salesPayment.txnId || salesPayment.txn || (event && event.transactionId);
      const additionalData = (event && event.salesFlatOrderPayment && event.salesFlatOrderPayment.additionalInformation) || null;
      const mode = (event && event.salesFlatOrderPayment && event.salesFlatOrderPayment.method) === 'Stripe' ? 'stripe' : 'razor_pay';

      const effectiveUsers = this.isUnlimitedPlan ? 1 : (Number(this.firstFormGroup.value.users || 0) + Number(this.addUserCount || 0));
      const additionalUsers = Math.max(0, Number(this.addUserCount || 0));

      const payload: any = {
        operationType: 'ADD_USERS',
        pricingId: this.selectedPricingId,
        billingCycle: this.billingCycle,
        users: effectiveUsers,
        additionalUsers: additionalUsers,
        mode: mode,
        txnId: orderTranscationId || this.firstFormGroup.value.txnId,
        paymentStatus: event && event.paypalPaymentStatus ? (event.paypalPaymentStatus === 'Successful' ? 'Paid' : event.paypalPaymentStatus) : 'Paid',
        phone: this.firstFormGroup.value.phone,
        email: this.firstFormGroup.value.email,
        name: this.buildCheckoutContactFields().name,
        country: this.buildCheckoutContactFields().country,
        address: this.buildCheckoutContactFields().address,
        city: this.buildCheckoutContactFields().city,
        state: this.buildCheckoutContactFields().state,
        pincode: this.firstFormGroup.value.pincode,
        productInfo: 'Crm',
        customValue: additionalData,
        transactionId: orderTranscationId,
        paymentModeType: this.firstFormGroup.value.paymentGateway
      };

      // Close modal and proceed to server checkout which will persist users and activate subscription
      this.addUser = false;
      this.makePayment(payload);
    } catch (e) {
      console.warn('Failed to handle add-user payment', e);
      this.snackBar.open('Payment processing failed. Please try again.', '×', { duration: 3000 });
    }
  }

  accountList: any = [];
  showPaymentOptions: boolean = false;
  paymentGateways: string[] = ['razor', 'stripe'];
  getAccountData(onDone?: () => void) {
    this.accountService.getAllAccountsBYtenant().subscribe(
      data => {

        this.accountList = data;

        if (this.accountList.length > 0) {


          const account = this.accountList[0];
          const preserveUpgradeSelection = !!this.pendingPlanSelectionState;
          const user = account.totalUserSubscrition;
          const summaryActive = this.subscriptionSummary?.subscriptionActive === true;
          const summaryMode = String(this.subscriptionSummary?.subscriptionMode || '').toUpperCase();


          this.accountUsersFromApi = Number(user || 0);
          this.currentUsers = this.accountUsersFromApi || 0;
          this.updateSelectedPlan();
          this.initializeUsers();

          if (summaryMode != 'EXPIRED') {
            this.hasActiveSubscription = true
          } else {
            this.hasActiveSubscription = false
          }
          // Autofill billing address fields when available
          const billingPatch: any = {};
          if (account.accountName) {
            billingPatch.storeName = account.accountName;
          } else if (account.domain) {
            billingPatch.storeName = account.domain;
          }
          if (account.phone) {
            billingPatch.phone = account.phone;
          }
          if ((account as any).email) {
            billingPatch.email = (account as any).email;
          }
          if ((account as any).address) {
            billingPatch.address = (account as any).address;
          }
          if ((account as any).pincode) {
            billingPatch.pincode = (account as any).pincode;
          }
          if ((account as any).country) {
            billingPatch.country = (account as any).country;
          }
          if ((account as any).state) {
            billingPatch.state = (account as any).state;
          }
          if ((account as any).city) {
            billingPatch.city = (account as any).city;
          }
          if (Object.keys(billingPatch).length > 0) {
            this.firstFormGroup.patchValue(billingPatch, { emitEvent: false });
            this.syncLocationSelectionsFromForm();

            // Update payment gateway based on country and currency
            if (billingPatch.country && billingPatch.country.toLowerCase() === 'india' && this.currency === 'INR') {
              this.firstFormGroup.patchValue({ paymentGateway: 'razor' });
            } else if (billingPatch.country) {
              this.firstFormGroup.patchValue({ paymentGateway: 'stripe' });
            }

            // If billing contact info exists, enable payment options in UI
            if (billingPatch.email || billingPatch.phone) {
              this.showPaymentOptions = true;
            }
          }
          if (account.billingCycle && !preserveUpgradeSelection) {
            this.billingCycle = account.billingCycle;
          }



          console.log(user);

        }

        if (onDone) {
          onDone();
        }
        // mark account load finished so template conditional rendering can proceed
        this.accountLoaded = true;
        // also set payment options visible if account indicates billing enabled
        if (!this.showPaymentOptions && this.accountList && this.accountList.length > 0) {
          const a = this.accountList[0];
          if (a && (a.email || a.phone || a.paymentMethod || a.showBillingPage)) {
            this.showPaymentOptions = true;
          }
        }
        this.updateShowPurchase();
      }
    )
  }

  loadBillingDetailsFromAccount() {
    this.accountService.getAlSubcriptionDetails().subscribe(
      data => {
        const accounts: any = data || [];
        if (!Array.isArray(accounts) && (accounts == null || Object.keys(accounts).length === 0)) {
          this.snackBar.open('No account billing data available', '×', { duration: 3000 });
          return;
        }
        const account = Array.isArray(accounts) ? (accounts[0] || {}) : accounts;
        const preserveUpgradeSelection = !!this.pendingPlanSelectionState;
        const billingPatch: any = {};
        if (account.accountName) billingPatch.storeName = account.accountName;
        else if (account.domain) billingPatch.storeName = account.domain;
        if (account.phone) billingPatch.phone = account.phone;
        if (account.email) billingPatch.email = account.email;
        if (account.address) billingPatch.address = account.address;
        if (account.pincode) billingPatch.pincode = account.pincode;
        if (account.country) billingPatch.country = account.country;
        if (account.state) billingPatch.state = account.state;
        if (account.city) billingPatch.city = account.city;
        if (Object.keys(billingPatch).length > 0) {
          this.firstFormGroup.patchValue(billingPatch, { emitEvent: false });
          this.syncLocationSelectionsFromForm();
          this.showPaymentOptions = Boolean(billingPatch.email || billingPatch.phone);
          // If account has a preferred payment method, map to form value
          if (account.paymentMethod) {
            const pm = String(account.paymentMethod || '').toLowerCase();
            if (pm.includes('razor')) {
              this.firstFormGroup.patchValue({ paymentGateway: 'razor' });
            } else if (pm.includes('stripe')) {
              this.firstFormGroup.patchValue({ paymentGateway: 'stripe' });
            }
          } else {
            // Default based on country and currency if no preferred method
            if (account.country && String(account.country).toLowerCase() === 'india' && this.currency === 'INR') {
              this.firstFormGroup.patchValue({ paymentGateway: 'razor' });
            } else if (account.country) {
              this.firstFormGroup.patchValue({ paymentGateway: 'stripe' });
            }
          }

          if (!preserveUpgradeSelection) {
            try {
              if (account.priceMonthly) this.availableMonthlyPrice = Number(account.priceMonthly);
              if (account.priceAnnually) this.availableAnnualPrice = Number(account.priceAnnually);
              this.firstFormGroup.patchValue({ priceMonthly: this.availableMonthlyPrice, priceAnnually: this.availableAnnualPrice }, { emitEvent: false });
              this.updateSelectedPlan();
              this.refreshPreview();
            } catch (e) {
              console.warn('Failed to merge account subscription into summary', e);
            }
          }

          //this.snackBar.open('Billing details loaded', '×', { duration: 2000 });
        }
        // Also fetch the latest payment_subscription row to patch current paid amounts into the active banner
        try {
          this.subscriptionsService.getSubscriptionHistoryPaged(0, 1, 'id,desc').subscribe((ps: any) => {
            if (!ps) return;
            let latest: any = null;
            if (Array.isArray(ps) && ps.length > 0) latest = ps[0];
            else if (ps.content && Array.isArray(ps.content) && ps.content.length > 0) latest = ps.content[0];
            else if (ps._embedded && ps._embedded.paymentSubscription && ps._embedded.paymentSubscription.length > 0) latest = ps._embedded.paymentSubscription[0];
            else if (typeof ps === 'object') {
              // sometimes API may return a single object
              if (ps.id || ps.amount || ps.total) latest = ps;
            }
            if (latest) {
              try {
                const preserveUpgradeSelection = !!this.pendingPlanSelectionState;
                // map common fields to subscriptionSummary and form so UI updates
                if (!this.subscriptionSummary) this.subscriptionSummary = {};

                // amounts
                const lastPaymentAmount = latest.grandTotal || latest.totalPrice || latest.amount || latest.netTotal || latest.paymentAmount;
                if (lastPaymentAmount != null) {
                  this.subscriptionSummary.lastPaymentAmount = Number(lastPaymentAmount);
                }

                const currentSubtotal = latest.totalPrice || latest.subtotal || latest.currentPrice;
                const currentGstAmount = latest.taxAmount || latest.gstAmount || latest.currentGstAmount;
                const currentGrandTotal = latest.grandTotal || latest.amount || latest.currentGrandTotal;
                const rowUsers = latest.finalUsers || latest.users || this.subscriptionSummary.totalLicensedUsers || this.subscriptionSummary.activeUsers;
                const currentUnitPrice = latest.planPricePerUser
                  || ((currentSubtotal != null && rowUsers != null && Number(rowUsers) > 0)
                    ? (Number(currentSubtotal) / Number(rowUsers))
                    : null);

                if (currentUnitPrice != null) {
                  this.subscriptionSummary.currentUnitPrice = Number(currentUnitPrice);
                }
                if (currentSubtotal != null) {
                  this.subscriptionSummary.currentPrice = Number(currentSubtotal);
                }
                if (currentGstAmount != null) {
                  this.subscriptionSummary.currentGstAmount = Number(currentGstAmount);
                }
                if (currentGrandTotal != null) {
                  this.subscriptionSummary.currentGrandTotal = Number(currentGrandTotal);
                }

                // dates
                const lastPaymentDate = latest.paymentDate || latest.createdOn || latest.createdDate || latest.txnDate;
                if (lastPaymentDate) {
                  this.subscriptionSummary.lastPaymentDate = lastPaymentDate;
                }

                if (latest.billingCycle) {
                  this.subscriptionSummary.billingCycle = latest.billingCycle;
                }

                // copy address/contact/country into subscriptionSummary and patch form
                if (latest.address) this.subscriptionSummary.address = latest.address;
                if (latest.city) this.subscriptionSummary.city = latest.city;
                if (latest.state) this.subscriptionSummary.state = latest.state;
                if (latest.pincode) this.subscriptionSummary.pincode = latest.pincode;
                if (latest.email) this.subscriptionSummary.email = latest.email;
                if (latest.phone) this.subscriptionSummary.phone = latest.phone;
                if (latest.country) this.subscriptionSummary.country = latest.country;

                const patch: any = {};
                if (latest.name) patch.storeName = latest.name;
                if (latest.phone) patch.phone = latest.phone;
                if (latest.email) patch.email = latest.email;
                if (latest.address) patch.address = latest.address;
                if (latest.city) patch.city = latest.city;
                if (latest.state) patch.state = latest.state;
                if (latest.pincode) patch.pincode = latest.pincode;
                if (latest.country) patch.country = latest.country;

                // Map txnId / transactionId into GST/VAT number field (some payloads use txnId)
                const txnVal = latest.txnId
                if (txnVal) {
                  this.subscriptionSummary.gstVatNo = txnVal;
                  this.subscriptionSummary.txnId = txnVal;
                  this.subscriptionSummary.transactionId = txnVal;
                  // patch commonly named gst fields and the txnId form control so the inputs show the value
                  patch.gstVatNo = txnVal;
                  patch.gstNo = txnVal;
                  patch.txnId = txnVal;
                  patch.transactionId = txnVal;
                }

                if (Object.keys(patch).length > 0) {
                  this.firstFormGroup.patchValue(patch, { emitEvent: false });
                  this.syncLocationSelectionsFromForm();
                }

                // payment gateway mapping
                const pmVal = latest.paymentModeType || latest.mode || latest.paymentMethod || latest.paymentMode || latest.paymentType;
                if (latest.currencySymbol) this.currencySymbol = latest.currencySymbol;
                if (latest.currencyCode) this.currencyCode = latest.currencyCode;
                if (pmVal) {
                  const pm = String(pmVal || '').toLowerCase();
                  if (pm.includes('razor')) this.firstFormGroup.patchValue({ paymentGateway: 'razor' }, { emitEvent: false });
                  else if (pm.includes('stripe')) this.firstFormGroup.patchValue({ paymentGateway: 'stripe' }, { emitEvent: false });
                }

                // keep raw record for debugging
                this.subscriptionSummary.lastPaymentRaw = latest;

                if (!preserveUpgradeSelection) {
                  if (latest.totalPrice) this.totalPrice = Number(latest.totalPrice);
                  if (latest.grandTotal) this.grandTotal = Number(latest.grandTotal);
                }

                // recompute derived fields and refresh preview/UI
                this.daysRemaining = Number(this.subscriptionSummary.daysRemaining || this.daysRemaining || 0);
                this.updateSelectedPlan();
                this.refreshPreview();
              } catch (e) { console.warn('Failed to apply latest payment subscription', e); }
            }
          }, err => { /* ignore */ });
        } catch (e) { console.warn('Failed to fetch latest payment subscription', e); }
      }, err => {
        //this.snackBar.open('Failed to load billing details', '×', { duration: 3000 });
      }
    );
  }

  private resolvePlanNameByPricingId(pricingId: any): string | null {
    if (pricingId == null) {
      return null;
    }
    const pricingKey = String(pricingId);
    const matchedPlan = (this.pricingList || []).find((item: any) => {
      const candidateIds = [item?.pricingId, item?.id];
      return candidateIds.some(id => id != null && String(id) === pricingKey);
    });
    return matchedPlan?.planName || matchedPlan?.pricingName || matchedPlan?.name || null;
  }

  updateShowPurchase() {
    const active = this.isTruthyFlag(this.subscriptionSummary?.subscriptionActive) || this.hasActiveSubscription;
    if (this.subscribtionType == 2 && this.canRenew) {
      this.showPurchase = true;
    } else {
      this.showPurchase = !active;
    }
    // mirror purchase visibility to the renewal flag used by template
    this.showRenwal = this.isUpgradeFromPlanSelection ? true : !!this.showPurchase;
    console.log('showPurchase:', this.showPurchase, 'showRenwal:', this.showRenwal, 'active:', active, 'canRenew:', this.canRenew, 'subscribtionType:', this.subscribtionType);
  }

  setFormInvalid() {

    this.firstFormGroup.markAllAsTouched();
  }


  ngOnDestroy() {
    this._onDestroy.next();
    this._onDestroy.complete();
    // Clear storage if navigating away from checkout
    if (this.router.url !== '/subscription/checkout') {
      try {
        sessionStorage.removeItem('pending_checkout_state');
      } catch (e) {}
    }
  }

  sanitizeUrl(u: string): string {
    return (u || '')
      .replace(/^\/+/, '')
      .replace(/[?#&=]/g, '-')
      .replace(/[^a-zA-Z0-9_-]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '')
      .toLowerCase();
  }

  private formatDateValue(value: any): string {
    if (!value) {
      return '-';
    }

    const parsedDate = new Date(value);
    if (Number.isNaN(parsedDate.getTime())) {
      return '-';
    }

    return parsedDate.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  }

  private normalizePlanSelectionPreview(preview: any): any {
    if (!preview) {
      return null;
    }

    const subtotal = preview.subtotal != null ? Number(preview.subtotal) : Number(preview.payable || 0);
    const gstAmount = preview.gstAmount != null ? Number(preview.gstAmount) : 0;
    const grandTotal = preview.grandTotal != null ? Number(preview.grandTotal) : Number(preview.finalPayable || 0);

    return Object.assign({}, preview, {
      subtotal: subtotal,
      grandTotal: grandTotal,
      nextPaymentAmount: preview.nextPaymentAmount != null ? Number(preview.nextPaymentAmount) : grandTotal,
      currentValidityDate: preview.currentValidityDate || preview.currentValidTill,
      projectedValidityDate: preview.projectedValidityDate || preview.projectedValidTill,
      pricingName: preview.pricingName || preview.targetPlan,
      users: Number(preview.users || 0),
      gstAmount: gstAmount
    });
  }

  private mergePlanSelectionPreview(preview: any): any {
    const normalizedPreview = this.normalizePlanSelectionPreview(preview || {});

    if (!this.isUpgradeFromPlanSelection) {
      return normalizedPreview;
    }

    const existingPreview = this.normalizePlanSelectionPreview(this.planSelectionCheckoutPreview || this.pricingPreview || {});
    if (!existingPreview) {
      return normalizedPreview;
    }

    const hasSubtotal = preview?.subtotal != null || preview?.payable != null;
    const hasGrandTotal = preview?.grandTotal != null || preview?.finalPayable != null;
    const hasGst = preview?.gstAmount != null;
    const hasCurrentValidity = preview?.currentValidityDate != null || preview?.currentValidTill != null || preview?.currentRenewalDate != null;
    const hasProjectedValidity = preview?.projectedValidityDate != null || preview?.projectedValidTill != null || preview?.nextPaymentDate != null || preview?.cycleEndDate != null;
    const hasPricingName = preview?.pricingName != null || preview?.targetPlan != null || preview?.planName != null;

    return Object.assign({}, existingPreview, normalizedPreview, {
      subtotal: hasSubtotal ? normalizedPreview?.subtotal : existingPreview?.subtotal,
      grandTotal: hasGrandTotal ? normalizedPreview?.grandTotal : existingPreview?.grandTotal,
      nextPaymentAmount: hasGrandTotal
        ? (normalizedPreview?.nextPaymentAmount != null ? normalizedPreview.nextPaymentAmount : normalizedPreview?.grandTotal)
        : (existingPreview?.nextPaymentAmount != null ? existingPreview.nextPaymentAmount : existingPreview?.grandTotal),
      gstAmount: hasGst ? normalizedPreview?.gstAmount : existingPreview?.gstAmount,
      currentValidityDate: hasCurrentValidity ? normalizedPreview?.currentValidityDate : existingPreview?.currentValidityDate,
      projectedValidityDate: hasProjectedValidity ? normalizedPreview?.projectedValidityDate : existingPreview?.projectedValidityDate,
      pricingName: hasPricingName ? normalizedPreview?.pricingName : existingPreview?.pricingName,
      users: normalizedPreview?.users || existingPreview?.users
    });
  }

  private patchEmbeddedUpgradeState(state: any): void {
    const preview = this.mergePlanSelectionPreview(state?.previewResponse || state?.previewData || {});
    const selectedUsers = Number(state?.users || this.firstFormGroup?.get('users')?.value || 1) || 1;
    this.planSelectionCheckoutPreview = preview;

    this.subscriptionSummary = Object.assign({}, this.subscriptionSummary || {}, {
      currentPlanName: state?.currentPlanName || this.subscriptionSummary?.currentPlanName,
      pricingId: Number(state?.selectedPricingId || this.selectedPricingId || this.subscriptionSummary?.pricingId || 0) || this.subscriptionSummary?.pricingId,
      pricingName: state?.selectedPlanName || preview?.pricingName || this.subscriptionSummary?.pricingName,
      billingCycle: state?.billingCycle || this.billingCycle || this.subscriptionSummary?.billingCycle,
      activeUsers: selectedUsers,
      totalLicensedUsers: selectedUsers,
      daysRemaining: Number(state?.daysRemaining || this.daysRemaining || this.subscriptionSummary?.daysRemaining || 0),
      subtotal: preview?.subtotal != null ? Number(preview.subtotal) : this.subscriptionSummary?.subtotal,
      gstAmount: preview?.gstAmount != null ? Number(preview.gstAmount) : this.subscriptionSummary?.gstAmount,
      grandTotal: preview?.grandTotal != null ? Number(preview.grandTotal) : this.subscriptionSummary?.grandTotal,
      nextPaymentAmount: preview?.grandTotal != null ? Number(preview.grandTotal) : this.subscriptionSummary?.nextPaymentAmount,
      nextPaymentDate: preview?.projectedValidityDate || preview?.nextPaymentDate || this.subscriptionSummary?.nextPaymentDate,
      subscriptionStartDate: preview?.cycleStartDate || this.subscriptionSummary?.subscriptionStartDate,
      cycleStartDate: preview?.cycleStartDate || this.subscriptionSummary?.cycleStartDate,
      cycleEndDate: preview?.projectedValidityDate || preview?.cycleEndDate || this.subscriptionSummary?.cycleEndDate,
      renewalDate: preview?.projectedValidityDate || preview?.cycleEndDate || this.subscriptionSummary?.renewalDate,
      priceMonthly: preview?.priceMonthly != null ? Number(preview.priceMonthly) : this.subscriptionSummary?.priceMonthly,
      priceAnnually: preview?.priceAnnually != null ? Number(preview.priceAnnually) : this.subscriptionSummary?.priceAnnually
    });

    this.currentUsers = Number(state?.currentUsers || this.currentUsers || 0);
    this.totalPrice = Number(preview?.subtotal || this.totalPrice || 0);
    this.gstAmount = Number(preview?.gstAmount || this.gstAmount || 0);
    this.grandTotal = Number(preview?.grandTotal || this.grandTotal || 0);

    // Detect unlimited plan from any source (OR logic) — plan DB flag may not be set yet,
    // but preview.unlimited uses maxUsers==null heuristic which is reliable.
    const isUnlimited = !!(state?.unlimited
      || state?.previewResponse?.unlimited
      || state?.previewData?.unlimited
      || preview?.unlimited);
    if (isUnlimited) {
      this.isUnlimitedPlan = true;
    }

    if (preview?.currencySymbol) {
      this.currencySymbol = preview.currencySymbol;
      this.currencyCode = preview.currencyCode || this.currencyCode;
    } else if (state?.currencySymbol) {
      this.currencySymbol = state.currencySymbol;
      this.currencyCode = state.selectedCurrency || state.currencyCode || this.currencyCode;
    }

    if (state?.billingCycle) {
      this.billingCycle = String(state.billingCycle).toLowerCase();
    }

    if (state?.selectedPricingId) {
      this.selectedPricingId = Number(state.selectedPricingId);
    }

    this.firstFormGroup.patchValue({
      pricingId: Number(state?.selectedPricingId || this.selectedPricingId || null),
      users: selectedUsers,
      priceMonthly: preview?.priceMonthly != null ? Number(preview.priceMonthly) : this.availableMonthlyPrice,
      priceAnnually: preview?.priceAnnually != null ? Number(preview.priceAnnually) : this.availableAnnualPrice
    }, { emitEvent: false });
  }

  private capturePlanSelectionState() {
    let state = (this.embeddedState || history.state || {}) as any;
    let previewPayload = state?.previewResponse || state?.previewData || null;
    let operationType = state?.operationType || state?.type || null;
    let looksLikePlanSelectionState = !!state
      && (state.source === 'subscription-plan-selection'
        || !!state.selectedPricingId
        || !!previewPayload
        || !!operationType);

    if (!looksLikePlanSelectionState) {
      try {
        const saved = sessionStorage.getItem('pending_checkout_state');
        if (saved) {
          state = JSON.parse(saved);
          previewPayload = state?.previewResponse || state?.previewData || null;
          operationType = state?.operationType || state?.type || null;
          looksLikePlanSelectionState = true;
        }
      } catch (e) {
        console.warn('Failed to parse pending checkout state from sessionStorage', e);
      }
    }

    if (!looksLikePlanSelectionState) {
      return;
    }

    // Persist so it survives page refreshes
    try {
      sessionStorage.setItem('pending_checkout_state', JSON.stringify(state));
    } catch (e) {
      console.warn('Failed to save pending checkout state to sessionStorage', e);
    }

    this.pendingPlanSelectionState = state;
    this.forcedOperationType = operationType || 'UPGRADE_PLAN';
    this.upgradePlantype = true;
    this.showRenwal = true;
    this.showPaymentOptions = true;
    this.subscribtionType = 2;

    if (state.selectedPricingId) {
      this.selectedPricingId = Number(state.selectedPricingId);
    }
    if (state.billingCycle) {
      this.billingCycle = state.billingCycle;
    }
    if (state.users) {
      this.firstFormGroup.patchValue({ users: Number(state.users) }, { emitEvent: false });
    }
    if (state.daysRemaining != null) {
      this.daysRemaining = Number(state.daysRemaining || 0);
    }
    if (previewPayload) {
      this.pricingPreview = this.normalizePlanSelectionPreview(previewPayload);
      this.planSelectionCheckoutPreview = this.pricingPreview;
      if (this.pricingPreview?.currencySymbol) {
        this.currencySymbol = this.pricingPreview.currencySymbol;
      }
    }

    if (state.currencySymbol) {
      this.currencySymbol = state.currencySymbol;
    }

    this.patchEmbeddedUpgradeState(state);
    this.loadBillingDetailsFromAccount();
  }
}
